/**
 * Chat widget — main web component. The custom element tag is brand-derived
 * at build time (WIDGET_TAG from brand.ts, e.g. `kobot-chat-widget`).
 *
 * Per docs/_archive/02-architecture.md sek. 9, docs/_archive/03-mvp-spec.md sek. 9 (FSM),
 * docs/_archive/07-mvp-minimal.md Chunk M.10.
 *
 * Attributes:
 *  - widget-key: tenant identifier z query stringu loadera `?w=` (required; patrz loader.ts)
 *  - api-base: opcjonalny override base URL (default: window.location.origin)
 *
 * Stany FSM (per docs/03 sek. 9):
 *  closed → opened (welcome) → user_typing → awaiting → idle
 *  + error, conversation_limit
 */

import { LitElement, html, css, nothing, noChange } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { classMap } from 'lit/directives/class-map.js';
import { computeSheetSnap } from '../util/sheet-drag.js';
import { directive, PartType, type ElementPart, type PartInfo } from 'lit/directive.js';
import { AsyncDirective } from 'lit/async-directive.js';
import EmblaCarousel, { type EmblaCarouselType, type EmblaOptionsType } from 'embla-carousel';
import {
  WidgetClient,
  WidgetApiException,
  type WidgetConfig,
  type WidgetTheme,
  type ProductCard,
  type ProductVariant,
  type OrderSnapshot,
  type OrderItem,
  type EscalationSignal,
  type ContactSignal,
  type CartOp,
  type CartSnapshot,
  type StoredMessage,
} from '../api/widget-client.js';
import {
  executeShopCartOp,
  claimCartOpOnce,
  resolveShopEndpoint,
  toEventOutcome,
  sanitizeShopMessage,
} from '../cart/external-cart.js';
import { WidgetSession } from '../state/widget-session.js';
import {
  resolveLocale,
  formatCartCounter,
  formatRelativeTime,
  interpolate,
  FALLBACK_I18N,
  type WidgetLocale,
  type WidgetI18n,
} from '../i18n/messages.js';
import { matchUrlPattern } from '../util/match-url-pattern.js';
import { resolveInk } from '../util/contrast-ink.js';
import { isValidPhone, isValidEmail, groupNational, formatIntlInput, OTHER_CC, PHONE_LEN, PHONE_CODES, LOCALE_TO_CC } from '../util/format-phone.js';
import { linkifyTokens } from '../util/format-links.js';
import { darkStyles } from './dark-theme.js';
import { brandLogo } from './brand-logo.js';
import { WIDGET_TAG, BRAND_NAME, BRAND_URL } from '../brand.js';
import {
  planOpen,
  readGlobalCustomer,
  normalizeNudge,
  registerWidgetInstance,
  unregisterWidgetInstance,
  type KobotOpenOptions,
  type KobotCustomerData,
  type KobotNudgeOptions,
  type NudgeSpec,
  type ConversationSource,
} from '../public-api.js';
import { publishSearchContext } from '../search-share.js';
import { safeUrl } from '../util/safe-url.js';

/** Mobile viewport breakpoint — shared by JS matchMedia checks (mirrors the CSS @media). */
const MOBILE_BREAKPOINT = '(max-width: 640px)';

/**
 * Cena „widoczna" na karcie: null/0 = cena ukryta (null-price-path). 0 nie jest
 * legalną ceną sprzedaży — ingest normalizuje 0→null, ale wiersze sprzed zmiany
 * żyją w DB do najbliższego syncu feedu, więc karta traktuje je identycznie.
 */
const visibleCents = (cents: number | null | undefined): number | null =>
  cents == null || cents === 0 ? null : cents;

/**
 * Wrapper na błąd streamu. `turnStarted` = serwer wyemitował JAKIKOLWIEK
 * event SSE — wtedy tura już biegnie server-side i resend by ją zdublował.
 * Celowo NIE patrzymy na zakumulowany tekst: backend emituje `replace` z
 * pustym contentem przy wykonywaniu tooli, więc "pusty bufor" nie znaczy
 * "tura nie ruszyła". `bannerSet` = in-band event {type:'error'} już ustawił
 * precyzyjny komunikat — send() nie nadpisuje go generycznym formatError.
 */
class StreamFailure extends Error {
  constructor(
    public readonly cause: unknown,
    public readonly turnStarted: boolean,
    public readonly bannerSet: boolean,
  ) {
    super('stream failed');
    this.name = 'StreamFailure';
  }
}

// Inline SVG icons (mockup parity). Stroke uses currentColor so they inherit text color.
const ICON_NEW_CHAT = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M4 5h16v11H9l-4 3.5V16H4z"></path><path d="M12 8v5M9.5 10.5h5"></path></svg>`;
const ICON_CLOSE = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18"></path></svg>`;
const ICON_SEND = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M5 12h14M13 6l6 6-6 6"></path></svg>`;
const ICON_CART = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M3 4h2l2 12h11l2-8H6"></path><circle cx="9" cy="20" r="1.3"></circle><circle cx="17" cy="20" r="1.3"></circle></svg>`;
const ICON_EYE = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"></path><circle cx="12" cy="12" r="2.6"></circle></svg>`;
const ICON_IMG = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="4" width="18" height="16" rx="2.5"></rect><circle cx="8.5" cy="9.5" r="1.6"></circle><path d="M21 16l-5-5L5 20"></path></svg>`;
const ICON_CHAT = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M4 5h16v11H9l-4 3.5V16H4z"></path><path d="M8.5 9.5h7M8.5 12.5h4"></path></svg>`;
// Cart bottom-sheet adds three new icons below; the bar/close/CTA reuse
// ICON_CART / ICON_CLOSE / ICON_SEND directly (no aliases).
const CHEVRON_UP_ICON = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 15l6-6 6 6"></path></svg>`;
const BACK_ICON = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 6l-6 6 6 6"></path></svg>`;
const CHECK_ICON = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M5 12.5l4.5 4.5L19 6.5"></path></svg>`;
/** Movement (px) under which a sheet pointer interaction counts as a tap, not a drag. */
const SHEET_TAP_PX = 6;

/** PHONE_CODES + LOCALE_TO_CC live in util/format-phone.ts (single source of truth,
 *  test-guarded that every LOCALE_TO_CC value ∈ PHONE_CODES). Derived orders below. */
/** Sorted by descending code length so splitPhone matches "+420" before "+4". */
const PHONE_CODES_BY_LEN = [...PHONE_CODES].sort((a, b) => b.code.length - a.code.length);
/** Selector display order: ascending by dial code ("Other" is appended at render time). */
const PHONE_CODES_SORTED = [...PHONE_CODES].sort((a, b) => Number(a.code.slice(1)) - Number(b.code.slice(1)));

/** Split a stored full phone into { dial code, grouped national }. International numbers are
 *  matched against the longest known code. A BARE national number defaults to +48 — the
 *  server only ever stores a bare number as Polish (PhoneValidator accepts it solely via its
 *  PL branch), so the widget locale's code would mislabel it (a PL number sent as +44/+49).
 *  An international number whose code isn't in the selector list maps to the "Other" option
 *  with the full "+<cc> national" preserved (so nothing is lost or mislabelled).
 *  `emptyCc` seeds the selector for an empty input (a fresh form → the locale's country). */
function splitPhone(full: string, emptyCc: string): { cc: string; national: string } {
  const s = (full ?? '').trim();
  const digits = s.replace(/\D/g, '');
  if (digits === '') return { cc: emptyCc, national: '' };
  if (s.startsWith('+') || /^00\d/.test(s)) {
    const norm = s.startsWith('00') ? digits.slice(2) : digits; // 00cc… → cc…
    for (const c of PHONE_CODES_BY_LEN) {
      const cd = c.code.slice(1);
      if (norm.startsWith(cd)) return { cc: c.code, national: groupNational(norm.slice(cd.length), 15 - cd.length) };
    }
    return { cc: OTHER_CC, national: '+' + norm }; // code not in the list → free-form "Other" field
  }
  return { cc: '+48', national: groupNational(digits, 9) }; // bare = Polish national
}

// Product carousel: grab-drag with momentum that settles onto a card. Embla
// (~4 kB gz, MIT) gives that via CSS transforms instead of a hand-rolled
// native-scroll engine. loop:false (2026-06-10): the loop-by-repositioning
// produced a visible jitter at the seam on mobile — plain bounded left-right
// drag, trimSnaps keeps the last card flush instead of leaving ghost snaps.
const EMBLA_OPTIONS: EmblaOptionsType = {
  loop: false,
  align: 'start',
  containScroll: 'trimSnaps',
  dragFree: false,
};

/**
 * Embla lifecycle as a Lit element-part directive: init Embla on the viewport when it
 * mounts, re-init when the product set changes (keyed), tear down on disconnect. A
 * directive (not a nested LitElement) keeps colours/i18n/handlers on this side of the
 * shadow boundary — no re-plumbing. Where ResizeObserver is missing (the test DOM) it
 * no-ops, so the markup degrades to a plain flex row instead of throwing.
 */
class EmblaDirective extends AsyncDirective {
  private api?: EmblaCarouselType;
  private node?: HTMLElement;
  private key = '';

  constructor(partInfo: PartInfo) {
    super(partInfo);
    if (partInfo.type !== PartType.ELEMENT) {
      throw new Error('emblaCarousel must be used as an element directive (no attribute name).');
    }
  }

  render(_key: string): typeof noChange {
    return noChange;
  }

  update(part: ElementPart, [key]: [string]): typeof noChange {
    const node = part.element as HTMLElement;
    if (this.api && this.node === node && this.key === key) return noChange;
    this.teardown();
    this.node = node;
    this.key = key;
    this.init(node);
    return noChange;
  }

  private init(node: HTMLElement): void {
    if (typeof ResizeObserver === 'undefined' || typeof requestAnimationFrame === 'undefined') return;
    // Defer a frame so the container + slides are laid out before Embla measures them.
    requestAnimationFrame(() => {
      if (this.node !== node || !node.isConnected) return;
      this.api = EmblaCarousel(node, EMBLA_OPTIONS);
    });
  }

  private teardown(): void {
    this.api?.destroy();
    this.api = undefined;
  }

  disconnected(): void {
    this.teardown();
  }

  reconnected(): void {
    if (this.node) this.init(this.node);
  }
}

const emblaCarousel = directive(EmblaDirective);

/** Memo of derived variant axes per product card — cards are immutable per message,
 * so this avoids rebuilding the axis matrix on every render (and every 60s tick). */
const variantAxesCache = new WeakMap<ProductCard, Array<{ key: string; values: string[] }>>();

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  guardrail?: string | null;
  products?: ProductCard[];
  /** Shop-cart operations of this turn (external_cart mode) — rendered as status chips. */
  cartOps?: CartOp[];
  // ISO-8601 server timestamp; present only on final bot/server messages (not on
  // the local greeting or a streaming placeholder) → drives the "X min temu" line.
  created_at?: string;
}

/**
 * Shape of the `demo-config` JSON attribute (panel Appearance preview). Carries
 * the non-theme demo data + the sample chat lines. Theme comes from the discrete
 * *-color / color-scheme / widget-position / entry-style attributes instead.
 */
interface DemoConfig {
  bot_name?: string;
  greeting?: string | null;
  avatar_url?: string | null;
  /** Sample transcript lines (i18n'd panel-side). */
  sample_user_msg?: string;
  /** Optional chrome i18n overrides (header status, input placeholder, send). */
  i18n?: Partial<WidgetI18n>;
}

@customElement(WIDGET_TAG)
export class ChatWidget extends LitElement {
  @property({ type: String, attribute: 'widget-key' }) widgetKey = '';

  @property({ type: String, attribute: 'api-base' }) apiBase = '';

  /**
   * Demo mode — the panel Appearance preview renders the REAL widget without any
   * backend. No widget-key / WidgetClient / WidgetSession / bootstrap; the config
   * is built in-memory from the discrete *-color / demo-config attrs and sending
   * is disabled. reflect → `:host([data-demo])` matches for the contained layout.
   */
  @property({ type: Boolean, attribute: 'data-demo', reflect: true }) demo = false;

  /** Live-theme attributes consumed in demo (mirror the Appearance form fields). */
  @property({ type: String, attribute: 'primary-color' }) primaryColorAttr = '';

  @property({ type: String, attribute: 'accent-color' }) accentColorAttr = '';

  /** Explicit font color on the primary/accent surface (demo) — overrides auto-contrast. */
  @property({ type: String, attribute: 'primary-ink' }) primaryInkAttr = '';

  @property({ type: String, attribute: 'accent-ink' }) accentInkAttr = '';

  @property({ type: String, attribute: 'color-scheme' }) colorSchemeAttr = '';

  @property({ type: String, attribute: 'widget-position' }) widgetPositionAttr = '';

  /** JSON blob with the non-theme demo data (bot name, greeting, avatar_url). */
  @property({ type: String, attribute: 'demo-config' }) demoConfigAttr = '';

  @property({ type: String, attribute: 'locale' }) localeAttr: WidgetLocale | '' = '';

  /** Prefill zalogowanego klienta sklepu — atrybuty na elemencie (manualny embed)… */
  @property({ type: String, attribute: 'customer-name' }) customerNameAttr = '';

  @property({ type: String, attribute: 'customer-email' }) customerEmailAttr = '';

  @property({ type: String, attribute: 'customer-phone' }) customerPhoneAttr = '';

  /** …albo przez window.<Brand>Widget.setCustomer() (single-tag loader). */
  private customerOverride: KobotCustomerData | null = null;

  /**
   * Origin set by the most recent open({source}) call (e.g. the <{brand}-search>
   * element) — consumed by ensureConversation() when it actually starts a NEW
   * conversation, then cleared. Overwritten (or cleared to null) on every open()
   * call so a stale source never leaks into an unrelated later conversation.
   */
  private pendingSource: ConversationSource | null = null;

  @state() private isOpen = false;
  /** 60s heartbeat so the "X min temu" lines age while the panel stays open. */
  private timeTicker?: ReturnType<typeof setInterval>;

  /**
   * Mobile-only bottom-sheet preview shown on first page load (auto-trigger
   * 1.5s after mount). Replaces launcher visually until user opens chat or
   * dismisses peek. Desktop flow is unchanged — peek never triggers.
   */
  @state() private peekVisible = false;

  private peekAutoTimeoutId: number | null = null;

  /**
   * Host-driven proactive bubble above the launcher (window.<Brand>Widget.nudge
   * or showNudge()). `nudge` = the active spec (scheduled or visible). Every
   * show condition is re-checked when the timer FIRES, never at scheduling time.
   * Spec: docs/superpowers/specs/2026-09-02-widget-nudge-design.md
   */
  private nudge: NudgeSpec | null = null;

  @state() private nudgeVisible = false;

  private nudgeTimeoutId: number | null = null;

  @state() private isLoading = false;

  @state() private locale: WidgetLocale = 'pl';

  @state() private config: WidgetConfig | null = null;

  @state() private conversationId: string | null = null;

  @state() private messages: ChatMessage[] = [];

  @state() private inputValue = '';

  /** Floating-island input value (entry_style=island). */
  @state() private islandValue = '';

  @state() private lastError: string | null = null;

  // Fatal bootstrap failure (e.g. unrecognized widget_key → backend 401).
  // Unlike `lastError` (transient — cleared before each send), this persists:
  // the widget cannot function without a successful bootstrap, so the banner
  // stays visible and the composer is disabled. Cleared only on a successful boot.
  @state() private bootError: string | null = null;

  // Scroll intent for the next post-render scroll: 'responseTop' pins the
  // visitor's message near the top so the reply reads from its start; 'bottom'
  // shows the newest content (open / restore / greeting). Imperative, not state.
  private nextScroll: 'bottom' | 'responseTop' = 'bottom';

  @state() private order: OrderSnapshot | null = null;

  /** product_id currently being added via a card "Dodaj do koszyka" click — disables that button. */
  @state() private addingProductId: string | null = null;

  /** Redirect-after-add (external_cart): notice widoczny w oknie opóźnienia przed nawigacją. */
  @state() private cartRedirectPending = false;

  /** Once-per-message guard dla redirectu — in-memory: po reloadzie opy są finalne, nic nie odpali. */
  private redirectedCartMessages = new Set<string>();
  /** Jedyny re-bootstrap po pending opie przy lokalnie wyłączonym koszyku (patrz runPendingCartOps). */
  private externalCartRefresh: Promise<void> | null = null;

  private cartRedirectTimer: number | null = null;

  /** Product card layout from bootstrap (panel „Widgety"): rich cards vs compact rows. */
  @state() private productDisplayMode: 'card' | 'compact' = 'card';

  // Per-card variant axis selections: product_id → { axisKey: chosenValue }.
  // Drives the variant selector, price display and the add-to-cart gate.
  @state() private variantSelections: Record<string, Record<string, string>> = {};

  // Owner choice (panel „Widgety"): 'buttons' (chips) | 'dropdown' (compact selects).
  @state() private variantSelectorStyle: 'buttons' | 'dropdown' = 'buttons';

  /** Cart bottom-sheet open/closed (overlay over the chat). */
  @state() private cartSheetOpen = false;

  // Refs for direct DOM writes during the drag (no per-frame Lit re-render) + fog.
  private sheetRef = createRef<HTMLElement>();
  private cartScrollRef = createRef<HTMLElement>();
  private formScrollRef = createRef<HTMLElement>();
  private cartFogRef = createRef<HTMLElement>();
  private formFogRef = createRef<HTMLElement>();

  /** When the sheet is open, true = on step 2 (order form); false = step 1 (cart). */
  @state() private orderFormOpen = false;

  @state() private orderFormSubmitting = false;

  /** General (non-field) order-form error: empty cart / already submitted / network. */
  @state() private orderFormError: string | null = null;

  /** Per-field inline errors shown under name / phone / email. */
  @state() private orderFieldErrors: { name?: string; phone?: string; email?: string } = {};

  // orderFormData.phone holds the NATIONAL part (grouped "xxx xxx xxx"); the dial code
  // is picked separately. The submitted/validated value is `${orderPhoneCc} ${phone}`.
  @state() private orderFormData = { name: '', phone: '', email: '', notes: '' };

  @state() private orderPhoneCc = '+48';
  /** Set once the user edits the order form, so re-opening (step 2 → back → CTA) does not
   *  clobber their input with the server draft. Reset on submit / when the draft is gone. */
  private orderFormDirty = false;

  // Escalation contact form — shown when the bot calls escalate_to_human (payload
  // signal). Submitting attaches the contact to the open escalation + notifies owner.
  @state() private escalationActive = false;

  @state() private escalationSubmitted = false;

  @state() private escalationFormSubmitting = false;

  @state() private escalationFormError: string | null = null;

  @state() private escalationFormData = { name: '', email: '', phone: '', message: '' };

  @state() private escalationPhoneCc = '+48';

  /**
   * Id wiadomości, przy której pojawiła się prośba o kontakt/eskalację. Formularz
   * renderuje się JAKO ELEMENT LISTY zaraz pod nią, więc kolejne wiadomości lądują
   * poniżej i formularz przewija się razem z rozmową — zamiast wisieć przyklejony
   * na dnie kontenera (zgłoszenie właściciela, 2026-08-13). null = brak kotwicy
   * (np. przywrócenie rozmowy po reloadzie) → render na końcu, jak dotąd.
   */
  @state() private contactAnchorId: string | null = null;

  @state() private escalationAnchorId: string | null = null;

  // Znaczniki OSTATNIEJ prośby z sygnałów serwera. Sygnały są aktywne w każdej
  // turze aż do domknięcia, więc samo "active" nie mówi, że bot poprosił znowu —
  // mówi o tym zmiana znacznika (wtedy formularz przeskakuje pod najnowszą
  // odpowiedź bota; prod 2026-08-31: wisiał przy pierwszej prośbie o wiele
  // wiadomości wyżej, a bot obiecywał "formularz poniżej").
  private contactRequestedAt: string | null = null;

  private escalationRequestedAt: string | null = null;

  // Contact form (np. leady) — shown when the bot needs contact (server `contact`
  // signal) and identity is incomplete. Mirrors the escalation form (no message field).
  @state() private contactActive = false;

  @state() private contactPurpose = 'lead';

  @state() private contactSubmitted = false;

  @state() private contactFormSubmitting = false;

  @state() private contactFormError: string | null = null;

  @state() private contactFormData = { name: '', email: '', phone: '' };

  @state() private contactPhoneCc = '+48';

  // Which identity fields the server still needs (drives which inputs render +
  // which are required) — a single x-pii action may need only one field.
  @state() private contactMissing: string[] = [];

  @state() private cookieInfoOpen = false;

  // "New conversation" confirmation: an in-widget modal (never a system confirm())
  // so the prompt stays inside the chat window and matches the widget's look.
  @state() private confirmNewOpen = false;

  // Owner globally disabled the widget (widget_enabled=false) → never render,
  // and public open() is a no-op.
  @state() private disabledByOwner = false;

  // Hidden only because the current path matched hidden_url_patterns. The
  // launcher hides, BUT an explicit host open() call overrides this.
  @state() private hiddenByUrlPattern = false;

  /** Combined: widget not shown on this page for either reason. */
  private get isHidden(): boolean {
    return this.disabledByOwner || this.hiddenByUrlPattern;
  }

  /**
   * Bootstrap-on-mount się zakończył (sukces lub błąd) — dopiero wtedy
   * renderujemy launcher. Daje to ~100-200ms opóźnienia na pageload, ale
   * eliminuje mignięcie gdy widget ma być ukryty. Failsafe na błąd bootstrap:
   * też ustawiamy true, żeby launcher i tak się pokazał (degraded fallback).
   */
  @state() private bootstrapAttempted = false;

  private client: WidgetClient | null = null;

  private session: WidgetSession | null = null;

  /** True when color_scheme=dark — drives conditional adoption of darkStyles. */
  private dark = false;

  static styles = css`
    :host {
      /* Two owner-chosen base colors (set via setProperty on bootstrap). Plain hex
         so a browser without color-mix() still gets the brand; everything below
         DERIVES from these so the whole widget re-themes from just two colors. */
      --primary: #4a2c5f;
      --accent: #e8593c;

      /* Auto-contrast ink for text drawn ON --primary / --accent surfaces.
         Computed per-colour in applyTheme() (inkFor → black on light bg, white
         on dark bg). Default white = the widget's historical hardcoded behaviour
         for the dark default brand colours above. */
      --primary-ink: #ffffff;
      --accent-ink: #ffffff;

      /* Plum family (from --primary) */
      --primary-2: #5d4070;
      --primary-2: color-mix(in srgb, var(--primary) 84%, #ffffff);
      --primary-dark: #2d1a3a;
      --primary-dark: color-mix(in srgb, var(--primary) 72%, #000000);

      /* Coral family (from --accent) */
      --accent-strong: #c8461d;
      --accent-strong: color-mix(in srgb, var(--accent) 82%, #000000);
      --accent-hover: #d14520;
      --accent-hover: color-mix(in srgb, var(--accent) 86%, #000000);
      --accent-soft: #fdeee8;
      --accent-soft: color-mix(in srgb, var(--accent) 12%, #ffffff);

      /* Secondary buttons (tonal plum, from --primary) — a defined secondary that
         pairs with the coral primary, instead of a washed-out white ghost button. */
      --secondary: #ece8ef;
      --secondary: color-mix(in srgb, var(--primary) 11%, #ffffff);
      --secondary-hover: #ddd6e3;
      --secondary-hover: color-mix(in srgb, var(--primary) 19%, #ffffff);
      --secondary-ink: #412754;
      --secondary-ink: color-mix(in srgb, var(--primary) 88%, #000000);
      --secondary-border: #d7d0dd;
      --secondary-border: color-mix(in srgb, var(--primary) 24%, #ffffff);

      /* Neutral surfaces — subtly tinted by the brand so hairlines/backgrounds
         track the chosen colors. Literal first = fallback if color-mix unsupported. */
      --bg: #faf7f6;
      --bg: color-mix(in srgb, var(--primary) 3%, #ffffff);
      --surface: #ffffff;
      --surface-2: #f5f0f3;
      --surface-2: color-mix(in srgb, var(--primary) 5%, #ffffff);
      --ink: #1f1b26;
      --ink-muted: #6b6877;
      --border: rgba(31, 27, 38, 0.1);
      --border: color-mix(in srgb, var(--primary) 13%, #ffffff);

      /* Gradients built from the families above */
      --grad-header: linear-gradient(165deg, var(--primary-2), var(--primary));
      --grad-accent: linear-gradient(140deg, var(--accent), var(--accent-strong));

      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      /* Reset WSZYSTKICH dziedziczonych właściwości typografii. Shadow DOM izoluje
         SELEKTORY hosta, ale właściwości DZIEDZICZONE (font-size/weight/style,
         line-height, text-align, color, letter-spacing…) przechodzą z hosta przez
         granicę shadow. Bez tego strona klienta (np. text-align:center na body albo
         własny font-size/weight) zmieniała wygląd treści widgetu — wycentrowane
         elementy + inny rozmiar/grubość pisma (lowisko.net 2026-06-19). Tu kotwiczymy
         typografię raz; potomkowie shadow dziedziczą JUŻ od widgetu, nie od strony. */
      font-size: 16px;
      font-weight: 400;
      font-style: normal;
      line-height: 1.5;
      text-align: left;
      letter-spacing: normal;
      word-spacing: normal;
      text-transform: none;
      white-space: normal;
      font-variant: normal;
      color: var(--ink);
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 2147483000; /* maksymalny żeby przebić strony klienta */
    }
    /* Per-tenant launcher position (widget_position w ui_theme). Default right; */
    /* set [data-position="left"] po bootstrap żeby przerzucić na lewo. */
    :host([data-position='left']) {
      right: auto;
      left: 24px;
    }

    /* Demo mode (panel Appearance preview): the widget fills its container instead
       of floating fixed in the corner, so it sits inside the preview box. */
    :host([data-demo]) {
      position: static;
      inset: auto;
      z-index: auto;
      display: block;
      width: 100%;
      height: 100%;
    }
    :host([data-demo]) .panel {
      width: 100%;
      height: 100%;
      max-width: none;
      max-height: none;
      position: relative;
    }

    .launcher {
      width: 62px;
      height: 62px;
      border-radius: 50%;
      background: var(--grad-accent);
      color: var(--accent-ink);
      border: none;
      /* UA stylesheet daje buttonom padding 1px 6px (shadow DOM nie ma resetu) —
         launcher-icon 100%×100% wypełniał content-box 50×60 = owal z tłem po bokach. */
      padding: 0;
      cursor: pointer;
      box-shadow: 0 10px 26px color-mix(in srgb, var(--accent) 42%, transparent);
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      animation: lb-float 3.4s ease-in-out infinite;
    }
    .launcher:hover {
      filter: brightness(1.06);
    }
    .launcher .ai-text {
      position: relative;
      z-index: 2;
      font-weight: 800;
      font-size: 21px;
      letter-spacing: 0.01em;
      color: var(--accent-ink);
      animation: lb-aipulse 2.6s ease-in-out infinite;
      text-shadow: 0 1px 2px rgba(120, 30, 0, 0.35);
    }
    .launcher .lb-ring {
      position: absolute;
      inset: 0;
      border-radius: 50%;
      /* Pełny akcent (bez rozcieńczenia) — 55% ginęło na jasnych tłach hosta. */
      border: 2px solid var(--accent);
      animation: lb-ring 2.6s ease-out infinite;
    }
    @keyframes lb-float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-5px); }
    }
    @keyframes lb-ring {
      0% { transform: scale(1); opacity: 0.9; }
      80%, 100% { transform: scale(1.55); opacity: 0; }
    }
    @keyframes lb-aipulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.08); }
    }
    @media (prefers-reduced-motion: reduce) {
      .launcher, .launcher .ai-text, .launcher .lb-ring, .launcher .launcher-icon, .nudge { animation: none; }
    }
    /* Custom launcher image (theme.launcher_icon_url) — fills the circle.
       Puls + warstwa nad ringiem: parytet z wariantem AI (zgłoszenie 2026-08-11 —
       przy obrazku zostawało samo lb-float, bez tętna). */
    .launcher-icon {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
      position: relative;
      z-index: 2;
      animation: lb-aipulse 2.6s ease-in-out infinite;
    }

    /* ============= Host nudge bubble (showNudge / window.<Brand>Widget.nudge) =============
       Absolutely positioned INSIDE :host (the fixed corner box, sibling of the
       launcher) so it inherits the launcher's side (left/right), the mobile
       safe-area and the per-tenant offset with zero media queries.
       width:max-content is mandatory — the host is only as wide as the launcher
       (62/52px); a shrink-to-fit bubble would wrap at the longest word. */
    .nudge {
      position: absolute;
      bottom: calc(100% + 12px);
      right: 0;
      width: max-content;
      max-width: min(320px, calc(100vw - 48px));
      box-sizing: border-box;
      background: var(--surface);
      color: var(--ink);
      border: 1px solid var(--border);
      border-radius: 14px;
      box-shadow: 0 12px 36px rgba(0, 0, 0, 0.18);
      animation: nudge-fade-up 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
    }
    /* Tail pointing at the launcher, same side as the anchor. */
    .nudge::after {
      content: '';
      position: absolute;
      bottom: -7px;
      right: 22px;
      width: 12px;
      height: 12px;
      background: var(--surface);
      border-right: 1px solid var(--border);
      border-bottom: 1px solid var(--border);
      transform: rotate(45deg);
    }
    /* Left-anchored launcher: mirror the bubble + tail. Scoped to the bubble
       variant — :host([data-position]) outranks .nudge--island (0,3,0 vs 0,1,0)
       and would drag the island bubble flush-left otherwise. */
    :host([data-position='left']) .nudge:not(.nudge--island) { right: auto; left: 0; }
    :host([data-position='left']) .nudge:not(.nudge--island)::after { right: auto; left: 22px; }
    .nudge-body {
      display: block;
      width: 100%;
      margin: 0;
      padding: 12px 36px 12px 14px;
      background: none;
      border: none;
      border-radius: 14px;
      font: inherit;
      font-size: 14px;
      line-height: 1.4;
      color: inherit;
      text-align: left;
      overflow-wrap: anywhere; /* a long URL in the message must not push past the bubble */
      cursor: pointer;
      transition: background 0.15s;
    }
    .nudge-body:hover { background: var(--surface-2); }
    .nudge-close {
      top: 8px;
      right: 8px;
      width: 24px;
      height: 24px;
    }
    .nudge-close svg { width: 12px; height: 12px; }
    /* Island variant: child of .island (fixed + centred) → centred above the bar,
       tail in the middle. Inherits the island's own mobile bottom rule. */
    .nudge--island {
      left: 0;
      right: 0;
      margin: 0 auto;
    }
    .nudge--island::after { right: auto; left: 50%; margin-left: -6px; }
    @keyframes nudge-fade-up {
      from { transform: translateY(8px); opacity: 0; }
      to   { transform: translateY(0);   opacity: 1; }
    }

    /* Floating Island (entry_style=island): a bottom-anchored input bar with an
       animated gradient border (tenant colors) to entice visitors to ask. Sits at
       the same bottom offset as the launcher bubble (24px). Submitting opens the chat. */
    .island {
      position: fixed;
      left: 50%;
      bottom: 24px;
      transform: translateX(-50%);
      width: min(440px, 90vw);
      padding: 2px; /* gradient border thickness */
      border-radius: 16px;
      background: linear-gradient(110deg, var(--accent), var(--primary), var(--accent));
      background-size: 220% 100%;
      animation: island-shimmer 3.2s linear infinite;
      box-shadow: 0 14px 44px rgba(0, 0, 0, 0.24);
    }
    @keyframes island-shimmer {
      to {
        background-position: 220% 0;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .island {
        animation: none;
      }
    }
    .island-inner {
      display: flex;
      align-items: center;
      gap: 8px;
      background: var(--surface);
      border-radius: 14px;
      padding: 10px 12px;
    }
    .island-input {
      flex: 1;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      color: var(--ink);
      font-size: 15px;
      font-family: inherit;
    }
    .island-input::placeholder {
      color: var(--ink-muted);
    }
    .island-send {
      flex-shrink: 0;
      border: none;
      cursor: pointer;
      background: var(--accent);
      color: var(--accent-ink);
      border-radius: 10px;
      padding: 8px 14px;
      font-size: 15px;
      font-weight: 600;
      transition: background 0.15s;
    }
    .island-send:hover:not(:disabled) {
      background: var(--accent-hover);
    }
    .island-send:disabled {
      opacity: 0.55;
      cursor: default;
    }

    /* Island layout: chat panel opens centered (desktop). Mobile keeps the
       existing fullscreen treatment from the max-width:640px media query. */
    @media (min-width: 641px) {
      :host([data-layout='island']) .panel {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    .panel {
      width: 450px;
      max-width: calc(100vw - 32px);
      /* Full height by default — top margin equals the 24px bottom offset of the
         launcher. Mobile is overridden to true fullscreen in the max-width:640px block. */
      height: calc(100dvh - 48px);
      max-height: calc(100dvh - 48px);
      background: var(--surface);
      border-radius: 24px;
      box-shadow: 0 6px 20px rgba(45, 26, 58, 0.1), 0 30px 70px rgba(45, 26, 58, 0.16);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      /* Positioning context so overflow:hidden clips absolutely-positioned overlays
         (the confirm scrim, cookie banner) to the 24px rounded corners — otherwise
         their containing block is :host and they show square corners at the rounding.
         No z-index → no new stacking context, so the header/footer dim layering holds. */
      position: relative;
    }

    header {
      background: var(--grad-header);
      color: var(--primary-ink);
      padding: 16px 18px;
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      position: relative;
      /* Above the confirm scrim (z-index 1) so the dim only covers the
         conversation area, not the header (cookie banner at z-index 5 still
         wins over both). */
      z-index: 2;
    }
    header::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 1px;
      background: rgba(255, 255, 255, 0.08);
    }
    .bot-avatar {
      width: 42px;
      height: 42px;
      border-radius: 13px;
      background: var(--grad-accent);
      color: var(--accent-ink);
      box-shadow: 0 4px 12px color-mix(in srgb, var(--accent) 35%, transparent);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 16px;
      flex-shrink: 0;
      overflow: hidden;
    }
    .bot-avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .bot-id {
      flex: 1;
      min-width: 0;
    }
    .bot-name {
      font-weight: 700;
      font-size: 15px;
      letter-spacing: -0.01em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .bot-status {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: color-mix(in srgb, var(--primary-ink) 72%, transparent);
      margin-top: 2px;
    }
    .bot-status .dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #4fe08a;
      box-shadow: 0 0 0 3px rgba(79, 224, 138, 0.25);
      flex-shrink: 0;
    }
    .header-actions {
      display: flex;
      gap: 4px;
    }
    .close-btn, .header-btn {
      width: 34px;
      height: 34px;
      border-radius: 10px;
      border: none;
      background: rgba(255, 255, 255, 0.1);
      color: var(--primary-ink);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      transition: background 0.15s;
    }
    .close-btn:hover, .header-btn:hover {
      background: rgba(255, 255, 255, 0.18);
    }
    .close-btn svg, .header-btn svg {
      width: 17px;
      height: 17px;
    }

    .scroll-area {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      background: var(--surface-2);
      display: flex;
      flex-direction: column;
      gap: 10px;
      /* Subtle, theme-adaptive scrollbar so desktop users can drag the thumb, not
         just wheel-scroll. --ink keeps it a faint neutral in both light + dark.
         Hidden on mobile (touch) in the @media block below. */
      scrollbar-width: thin; /* Firefox */
      scrollbar-color: color-mix(in srgb, var(--ink) 22%, transparent) transparent;
    }
    .scroll-area::-webkit-scrollbar {
      width: 8px;
    }
    .scroll-area::-webkit-scrollbar-track {
      background: transparent;
    }
    .scroll-area::-webkit-scrollbar-thumb {
      background: color-mix(in srgb, var(--ink) 20%, transparent);
      border-radius: 8px;
      /* 2px transparent border + padding-box clip → a slim, floating thumb (~4px). */
      border: 2px solid transparent;
      background-clip: padding-box;
    }
    .scroll-area::-webkit-scrollbar-thumb:hover {
      background: color-mix(in srgb, var(--ink) 34%, transparent);
      background-clip: padding-box;
    }

    .msg {
      max-width: 100%;
      padding: 11px 14px;
      border-radius: 16px;
      font-size: 14px;
      line-height: 1.5;
      /* Newlines are rendered as <br> by renderMessageContent() (parity with
         Message::renderedContent) — NOT via white-space:pre-wrap, which would
         also render the Lit template's own indentation as blank space/gaps. */
      word-break: break-word;
    }
    .msg.user {
      align-self: flex-end;
      background: var(--primary);
      color: var(--primary-ink);
      border-bottom-right-radius: 5px;
    }
    .msg.assistant {
      align-self: flex-start;
      background: var(--surface);
      color: var(--ink);
      border: 1px solid var(--border);
      border-bottom-left-radius: 5px;
      box-shadow: 0 1px 1px rgba(45, 26, 58, 0.03);
    }
    .msg.assistant.guardrail {
      border-color: color-mix(in srgb, var(--accent) 35%, transparent);
    }
    /* Markdown lists ("- "/"* ") rendered as real bullets instead of a literal "-". */
    .msg .msg-list {
      margin: 6px 0;
      padding-left: 20px;
      list-style: disc;
    }
    .msg .msg-list li { margin: 2px 0; }
    .msg .msg-list li::marker { color: var(--accent-strong); }
    /* Clickable links inside message bubbles. Inherit colour by default (white
       on the user bubble), accent on the assistant bubble; wrap long URLs. */
    .msg-link {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
      font-weight: 600;
      word-break: break-word;
    }
    .msg.assistant .msg-link {
      color: var(--accent-strong);
    }
    .guardrail-badge {
      display: inline-block;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 2px 6px;
      border-radius: 4px;
      background: var(--accent-soft);
      color: var(--accent-strong);
      margin-bottom: 4px;
    }
    /* "X minut temu" under a bot bubble — aligned to the bubble's left edge,
       faint so it never competes with the message. */
    .msg-time {
      align-self: flex-start;
      margin: 3px 0 2px 6px;
      font-size: 10px;
      color: var(--ink-muted);
      opacity: 0.75;
      cursor: default;
    }

    .product-group {
      max-width: 100%;
      margin-top: 12px;
    }

    /* ---- Rich card (card mode) ---- */
    .pcard {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 18px;
      overflow: hidden;
      box-shadow: 0 2px 8px color-mix(in srgb, var(--primary) 6%, transparent);
      display: flex;
      flex-direction: column;
    }
    .pcard .ph {
      height: 128px;
      margin: 16px 12px 0;
      border-radius: 12px;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      flex-shrink: 0;
    }
    .pcard .ph img {
      width: 100%;
      height: 100%;
      object-fit: var(--product-img-fit, cover);
    }
    .pcard .ph-ph {
      color: var(--ink-muted);
      opacity: 0.55;
    }
    .pcard .ph-ph svg {
      width: 42px;
      height: 42px;
    }
    .pcard .pb {
      padding: 14px 15px;
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    .pn-link {
      color: inherit;
      text-decoration: none;
    }
    .pn-link:hover {
      text-decoration: underline;
    }
    .pcard .pn {
      font-weight: 700;
      font-size: 15px;
      letter-spacing: -0.01em;
      color: var(--ink);
      line-height: 1.3;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .pcard .pd {
      font-size: 12.5px;
      color: var(--ink-muted);
      margin-top: 6px;
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    /* margin-top:auto pins the price + actions to the bottom so they line up across
       cards of equal height regardless of whether a description is present. */
    .pcard .pprice {
      font-size: 19px;
      font-weight: 800;
      letter-spacing: -0.02em;
      color: var(--ink);
      margin-top: auto;
      padding-top: 13px;
    }
    .pcard .pacts {
      display: flex;
      gap: 8px;
      margin-top: 14px;
    }

    /* ---- Shared card-mode action buttons ---- */
    .pact {
      flex: 1;
      min-width: 0;
      height: 42px;
      border-radius: 12px;
      font-family: inherit;
      font-size: 13.5px;
      font-weight: 600;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      border: 1px solid transparent;
      white-space: nowrap;
      text-decoration: none;
      transition: background 0.15s, border-color 0.15s, opacity 0.15s;
    }
    .pact svg {
      width: 16px;
      height: 16px;
    }
    .pact.details {
      background: var(--secondary);
      color: var(--secondary-ink);
      border-color: var(--secondary-border);
    }
    .pact.details:hover {
      background: var(--secondary-hover);
    }
    .pact.add {
      background: var(--accent-strong);
      color: var(--accent-ink);
      box-shadow: 0 3px 10px color-mix(in srgb, var(--accent) 25%, transparent);
    }
    .pact.add:hover {
      background: var(--accent-hover);
    }
    .pact.add:disabled {
      opacity: 0.55;
      cursor: default;
    }

    /* ---- Variant selector (size/colour chips) ---- */
    .pvariants {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 10px;
    }
    .pvaxis {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
    .pvaxis-label {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.02em;
      color: var(--ink-muted);
      text-transform: uppercase;
    }
    .pvaxis-opts {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }
    .pvchip {
      font-family: inherit;
      font-size: 12.5px;
      font-weight: 600;
      padding: 5px 11px;
      border-radius: 9px;
      cursor: pointer;
      color: var(--ink);
      background: var(--surface-2);
      border: 1px solid var(--border);
      transition: background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s;
    }
    .pvchip:hover:not(:disabled):not(.sel) {
      border-color: color-mix(in srgb, var(--accent) 45%, var(--border));
    }
    .pvchip.sel {
      background: var(--accent-soft);
      color: var(--accent-strong);
      border-color: color-mix(in srgb, var(--accent) 55%, transparent);
    }
    .pvchip:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      text-decoration: line-through;
    }

    /* ---- Variant selector: dropdown style (compact, label + select inline) ---- */
    .pvariants-dd {
      gap: 6px;
    }
    .pvaxis-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .pvaxis-row .pvaxis-label {
      flex-shrink: 0;
      min-width: 64px;
    }
    .pvselect {
      flex: 1;
      min-width: 0;
      font-family: inherit;
      font-size: 13px;
      height: 34px;
      padding: 0 9px;
      border-radius: 9px;
      border: 1px solid var(--border);
      background: var(--surface-2);
      color: var(--ink);
      cursor: pointer;
    }
    .pvselect:focus {
      outline: none;
      border-color: color-mix(in srgb, var(--accent) 45%, var(--border));
      background: var(--surface);
    }

    /* ---- Carousel (card mode, multiple products) — Embla viewport/container ---- */
    .embla {
      overflow: hidden;
      cursor: grab;
      /* Mouse-drag scroll must not select card text / ghost-drag images (desktop). */
      user-select: none;
      -webkit-user-select: none;
    }
    .embla:active {
      cursor: grabbing;
    }
    .embla img {
      -webkit-user-drag: none;
      user-drag: none;
    }
    .embla__container {
      display: flex;
      /* Spacing comes from slide padding (below), not flex gap — flex gap was not
         honoured at the snap seams (it glued two cards together). The negative
         margin offsets the first slide's padding so the first card sits flush left. */
      margin-left: -10px;
      align-items: stretch; /* slides equal height → card price/buttons line up */
      touch-action: pan-y; /* vertical page scroll passes through; Embla owns horizontal */
    }
    .embla__slide {
      flex: 0 0 270px;
      min-width: 0;
      padding-left: 10px;
      display: flex;
    }
    .embla__slide > .pcard {
      flex: 1 1 auto;
      min-width: 0;
    }
    .embla__slide .pcard .ph {
      height: 108px;
    }

    /* ---- Compact row (compact mode) ---- */
    .ptile {
      display: flex;
      border: 1px solid var(--border);
      border-radius: 13px;
      overflow: hidden;
      background: var(--surface);
    }
    .ptile + .ptile {
      margin-top: 8px;
    }
    .ptile .pt-img {
      width: 76px;
      flex-shrink: 0;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    .ptile .pt-img img {
      width: 100%;
      height: 100%;
      object-fit: var(--product-img-fit, cover);
    }
    .ptile .pt-ph {
      color: var(--ink-muted);
      opacity: 0.55;
    }
    .ptile .pt-ph svg {
      width: 26px;
      height: 26px;
    }
    .ptile .pt-b {
      flex: 1;
      min-width: 0;
      padding: 9px 11px;
      display: flex;
      flex-direction: column;
    }
    .ptile .pt-n {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--ink);
      line-height: 1.3;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .ptile .pt-desc {
      font-size: 11px;
      color: var(--ink-muted);
      line-height: 1.4;
      margin-top: 3px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .ptile .pt-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-top: auto;
      padding-top: 8px;
    }
    .ptile .pt-p {
      font-size: 13px;
      font-weight: 800;
      letter-spacing: -0.01em;
      color: var(--ink);
    }
    .pt-iacts {
      display: flex;
      gap: 6px;
      flex-shrink: 0;
    }
    .pt-ico {
      width: 34px;
      height: 34px;
      border-radius: 9px;
      border: 1px solid var(--border);
      background: var(--surface);
      color: var(--ink);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      text-decoration: none;
      transition: background 0.15s, opacity 0.15s;
    }
    .pt-ico svg {
      width: 17px;
      height: 17px;
    }
    .pt-ico:hover {
      background: var(--surface-2);
    }
    .pt-ico.cart {
      background: var(--accent-strong);
      color: var(--accent-ink);
      border-color: var(--accent-strong);
    }
    .pt-ico.cart:hover {
      background: var(--accent-hover);
    }
    .pt-ico:disabled {
      opacity: 0.55;
      cursor: default;
    }

    /* Chipy startowe żyją WEWNĄTRZ okna rozmowy, pod dymkiem powitania
       (standard rynkowy; wcześniej osobny pas nad inputem). Padding/gap daje
       scroll-area; lekki wcięty margines wyrównuje je z dymkami bota. */
    .suggestions {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      padding: 0;
      align-self: flex-start;
    }
    .suggestion {
      background: var(--accent-soft);
      border: 1px solid color-mix(in srgb, var(--accent) 22%, transparent);
      color: var(--accent-strong);
      padding: 7px 13px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
      /* <button> ma UA text-align:center, którego reset text-align:left na :host
         nie nadpisuje — długie pytania zawijające się do 2 linii (częste na mobile)
         wychodziły wyśrodkowane. Wymuszamy wyrównanie do lewej. */
      text-align: left;
      cursor: pointer;
      transition: all 0.15s;
    }
    .suggestion:hover {
      background: color-mix(in srgb, var(--accent) 18%, #ffffff);
    }

    .typing {
      align-self: flex-start;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 14px;
      border-bottom-left-radius: 4px;
      padding: 10px 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .typing .avatar {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: var(--accent);
      color: var(--accent-ink);
      font-size: 9px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .typing .label {
      font-size: 12px;
      color: #6b7280;
    }
    .typing .dots {
      display: flex;
      gap: 3px;
      margin-left: 2px;
    }
    .typing .dots span {
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--accent);
      animation: typing-bounce 1.4s infinite ease-in-out;
    }
    .typing .dots span:nth-child(2) { animation-delay: 0.15s; }
    .typing .dots span:nth-child(3) { animation-delay: 0.3s; }
    @keyframes typing-bounce {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
      30% { transform: translateY(-3px); opacity: 1; }
    }

    .error-banner {
      background: #fef2f2;
      color: #991b1b;
      padding: 8px 12px;
      font-size: 12px;
      border-top: 1px solid #fecaca;
    }

    /* ===== Collapsed cart bar (in-flow, above the composer) ===== */
    .cart-bar {
      position: relative;
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 11px 14px;
      background: var(--surface);
      border-top: 1px solid var(--border);
      cursor: pointer;
      transition: background 0.14s;
    }
    .cart-bar:hover { background: var(--surface-2); }
    .cart-bar .cbi {
      position: relative;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: var(--accent-soft);
      color: var(--accent-strong);
      display: grid;
      place-items: center;
      flex-shrink: 0;
    }
    .cart-bar .cbi svg { width: 19px; height: 19px; }
    .cart-bar .cbt { flex: 1; min-width: 0; }
    .cart-bar .cbt .l1 { font-size: 13px; font-weight: 700; color: var(--ink); }
    .cart-bar .cbt .l2 { font-size: 11.5px; color: var(--ink-muted); margin-top: 1px; }
    .cart-bar .cbv {
      font-size: 15px;
      font-weight: 800;
      letter-spacing: -0.01em;
      color: var(--ink);
      white-space: nowrap;
    }
    .cart-bar .chev { color: var(--ink-muted); display: grid; place-items: center; }
    .cart-bar .chev svg { width: 18px; height: 18px; }

    /* External mode: cart-bar as a plain link to the shop's cart page. */
    a.shop-cart-link { text-decoration: none; }
    a.shop-cart-link .chev svg { transform: rotate(90deg); }

    /* ===== Shop-cart op status chips (external_cart) ===== */
    .cart-op-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin: 6px 0 2px;
    }
    .cart-op-chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 4px 10px;
      border-radius: 999px;
      font-size: 11.5px;
      font-weight: 600;
      border: 1px solid var(--border);
      background: var(--surface);
      color: var(--ink-muted);
    }
    .cart-op-chip.ok {
      background: var(--accent-soft);
      border-color: transparent;
      color: var(--accent-strong);
    }
    .cart-op-chip.error,
    .cart-op-chip.rejected {
      background: color-mix(in srgb, #dc2626 12%, transparent);
      border-color: transparent;
      color: #b91c1c;
    }
    .cart-op-chip.rejected {
      /* Shop-provided message can run up to 200 chars — must wrap, not overflow. */
      white-space: normal;
      max-width: 100%;
    }
    .cart-op-chip.pending::before {
      content: '';
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: 2px solid var(--ink-muted);
      border-top-color: transparent;
      animation: cart-op-spin 0.8s linear infinite;
    }
    @keyframes cart-op-spin { to { transform: rotate(360deg); } }

    /* ===== Cart-contents card (external_cart view op) ===== */
    .cart-view-card {
      flex-basis: 100%;
      border: 1px solid var(--border);
      border-radius: 14px;
      background: var(--surface);
      padding: 10px 12px;
      font-size: 12.5px;
      color: var(--ink);
    }
    .cart-view-card .cv-head {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 6px;
    }
    .cart-view-card .cv-title { font-weight: 700; font-size: 12.5px; }
    .cart-view-card .cv-count { font-size: 11px; color: var(--ink-muted); }
    .cart-view-card .cv-item {
      display: flex;
      align-items: baseline;
      gap: 8px;
      padding: 3px 0;
    }
    .cart-view-card .cv-name { flex: 1; min-width: 0; overflow-wrap: anywhere; }
    .cart-view-card .cv-line { color: var(--ink-muted); white-space: nowrap; font-variant-numeric: tabular-nums; }
    .cart-view-card .cv-note { color: var(--ink-muted); font-size: 12px; }
    .cart-redirect-note { display: flex; justify-content: center; padding: 4px 0 2px; }

    /* ===== Bottom-sheet overlay ===== */
    .cart-scrim {
      position: absolute;
      inset: 0;
      background: rgba(18, 30, 22, 0.42);
      backdrop-filter: blur(1px);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.28s ease;
      z-index: 10;
    }
    .cart-scrim.open { opacity: 1; pointer-events: auto; }
    .cart-sheet {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 90%;
      background: var(--surface);
      border-radius: 22px 22px 0 0;
      box-shadow: 0 -10px 40px rgba(18, 30, 22, 0.22);
      z-index: 11;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transform: translateY(100%);
      transition: transform 0.32s cubic-bezier(0.22, 0.61, 0.36, 1);
      pointer-events: none; /* closed/off-screen sheet must never intercept the bar tap */
    }
    .cart-sheet.open { transform: translateY(0); pointer-events: auto; }
    .cart-sheet.dragging { transition: none; } /* finger-tracking writes transform live */
    .cart-grip {
      display: flex;
      justify-content: center;
      padding: 9px 0 3px;
      flex-shrink: 0;
      cursor: grab;
      touch-action: none;
    }
    .cart-grip::before {
      content: '';
      width: 38px;
      height: 4px;
      border-radius: 3px;
      background: color-mix(in srgb, var(--ink-muted) 45%, transparent);
    }

    .cart-steps {
      flex: 1;
      display: flex;
      width: 200%;
      min-height: 0;
      transition: transform 0.34s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .cart-sheet.step2 .cart-steps { transform: translateX(-50%); }
    .cart-step {
      width: 50%;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }

    .cart-head {
      display: flex;
      align-items: center;
      gap: 11px;
      padding: 4px 16px 14px;
      flex-shrink: 0;
      border-bottom: 1px solid var(--border);
    }
    .cart-head .title { flex: 1; min-width: 0; }
    .cart-head .title .t { font-weight: 800; font-size: 16px; letter-spacing: -0.01em; color: var(--ink); }
    .cart-head .title .s { font-size: 12px; color: var(--ink-muted); margin-top: 1px; }
    .cart-head .icon-btn {
      width: 32px;
      height: 32px;
      border-radius: 10px;
      border: 1px solid var(--border);
      background: var(--surface);
      color: var(--ink-muted);
      display: grid;
      place-items: center;
      cursor: pointer;
      flex-shrink: 0;
    }
    .cart-head .icon-btn:hover { background: var(--surface-2); color: var(--ink); }
    .cart-head .icon-btn svg { width: 17px; height: 17px; }

    .cart-scrollwrap { flex: 1; position: relative; min-height: 0; }
    .cart-scroll {
      position: absolute;
      inset: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .cart-scroll::-webkit-scrollbar { display: none; }
    .cart-fog {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 56px;
      pointer-events: none;
      background: linear-gradient(to top, var(--surface) 8%, color-mix(in srgb, var(--surface) 85%, transparent) 42%, transparent);
      opacity: 0;
      transition: opacity 0.22s ease;
      z-index: 2;
    }
    .cart-fog.show { opacity: 1; }

    /* read-only line items */
    .cart-items .item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 13px 16px;
      border-bottom: 1px solid var(--border);
    }
    .cart-items .item .ct {
      position: relative;
      width: 42px;
      height: 42px;
      border-radius: 9px;
      overflow: hidden;
      flex-shrink: 0;
      background: var(--surface-2);
      display: grid;
      place-items: center;
      color: var(--ink-muted);
    }
    .cart-items .item .ct svg { width: 18px; height: 18px; opacity: 0.5; }
    .cart-items .item .ct img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
    .cart-items .item .name { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .cart-items .item .pname { font-weight: 600; font-size: 13.5px; color: var(--ink); line-height: 1.3; overflow: hidden; text-overflow: ellipsis; }
    .cart-items .item .vlabel { font-size: 11.5px; color: var(--ink-muted); }
    .cart-items .item .qty { font-size: 13px; font-weight: 600; color: var(--ink-muted); flex-shrink: 0; font-variant-numeric: tabular-nums; }
    .cart-items .item .line-total { font-size: 13.5px; font-weight: 700; color: var(--ink); white-space: nowrap; flex-shrink: 0; font-variant-numeric: tabular-nums; }

    /* form fields scroll region (reuses .order-form input/label styling) */
    .order-form-fields { padding: 14px 16px; }

    /* sticky footer (both steps) */
    .cart-foot {
      flex-shrink: 0;
      background: var(--surface);
      border-top: 1px solid var(--border);
      padding: 13px 16px calc(13px + env(safe-area-inset-bottom, 0px));
      position: relative;
      z-index: 3;
    }
    .cart-foot .ttl { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 12px; }
    .cart-foot .ttl .l { font-size: 14px; font-weight: 800; color: var(--ink); }
    .cart-foot .ttl .v { font-size: 21px; font-weight: 800; letter-spacing: -0.02em; color: var(--ink); font-variant-numeric: tabular-nums; }
    .cart-cta {
      width: 100%;
      height: 50px;
      border: none;
      border-radius: 14px;
      background: var(--accent-strong);
      color: var(--accent-ink);
      font-family: inherit;
      font-weight: 700;
      font-size: 15px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 9px;
      box-shadow: 0 5px 16px color-mix(in srgb, var(--accent) 32%, transparent);
      transition: opacity 0.14s;
    }
    .cart-cta:hover { opacity: 0.93; }
    .cart-cta:disabled { opacity: 0.5; cursor: not-allowed; }
    .cart-cta svg { width: 18px; height: 18px; }

    .order-form {
      flex-shrink: 0;
      background: var(--surface);
      border-top: 1px solid var(--border);
      padding: 14px 16px;
    }
    /* Step-2 order form fills the sheet step as a flex column: head is fixed (the
       .cart-head above), fields scroll inside .cart-scroll, footer stays sticky. The
       base .order-form rule above still gives the escalation card its bg + padding;
       here we override padding/border so the step lays out flush. */
    .cart-step .order-form {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      padding: 0;
      border-top: none;
    }
    .cart-step .order-form > form {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }
    .order-form-header {
      flex-shrink: 0;
    }
    .order-form-fields {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      scrollbar-width: none;
    }
    .order-form-fields::-webkit-scrollbar {
      display: none;
    }
    .order-form-header h3 {
      margin: 0 0 4px;
      font-size: 14px;
      font-weight: 600;
    }
    .order-form-header p {
      margin: 0 0 12px;
      font-size: 12px;
      color: var(--ink-muted);
    }
    .order-form label {
      display: block;
      margin-bottom: 10px;
      font-size: 12px;
    }
    .order-form label span {
      display: block;
      margin-bottom: 5px;
      color: var(--ink);
      font-weight: 600;
    }
    .order-form label span small {
      font-weight: 500;
      color: var(--ink-muted);
    }
    .order-form input,
    .order-form textarea {
      width: 100%;
      height: 42px;
      padding: 0 13px;
      border: 1px solid var(--border);
      border-radius: 11px;
      background: var(--surface-2);
      color: var(--ink);
      font-size: 13.5px;
      font-family: inherit;
      box-sizing: border-box;
    }
    .order-form input:focus,
    .order-form textarea:focus {
      outline: none;
      border-color: color-mix(in srgb, var(--accent) 45%, var(--border));
      background: var(--surface);
    }
    .order-form textarea {
      height: auto;
      min-height: 66px;
      padding: 11px 13px;
      resize: vertical;
    }
    /* Validity checkmark inside phone/email fields (shown when the value is valid). */
    .order-form .field-input { position: relative; display: block; }
    .order-form .field-input input { padding-right: 40px; }
    /* Phone: dial-code selector + national input side by side. */
    .order-form .phone-row { display: flex; gap: 8px; }
    .order-form .phone-row .field-input { flex: 1; min-width: 0; }
    .order-form .phone-cc {
      flex-shrink: 0;
      height: 42px;
      padding: 0 8px;
      border: 1px solid var(--border);
      border-radius: 11px;
      background: var(--surface-2);
      color: var(--ink);
      font-size: 13.5px;
      font-family: inherit;
      cursor: pointer;
    }
    .order-form .phone-cc:focus { outline: none; border-color: color-mix(in srgb, var(--accent) 45%, var(--border)); background: var(--surface); }
    .order-form .field-check {
      position: absolute;
      top: 50%;
      right: 12px;
      transform: translateY(-50%);
      display: grid;
      place-items: center;
      color: #1f9d57;
      pointer-events: none;
    }
    .order-form .field-check svg { width: 16px; height: 16px; }
    .order-form .field-error {
      display: block;
      margin-top: 5px;
      font-size: 11.5px;
      font-weight: 500;
      color: #b91c1c;
    }
    .order-form-error {
      padding: 8px 10px;
      background: #fef2f2;
      border: 1px solid #fecaca;
      color: #991b1b;
      border-radius: 6px;
      font-size: 12px;
      margin-bottom: 10px;
    }
    .order-form-actions {
      display: flex;
      flex-shrink: 0;
      gap: 8px;
      justify-content: flex-end;
      margin-top: 8px;
      padding-top: 4px;
    }
    .order-form-actions button {
      padding: 8px 14px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      border: none;
    }
    .order-form-actions .submit {
      background: var(--accent-strong);
      color: var(--accent-ink);
      box-shadow: 0 4px 12px color-mix(in srgb, var(--accent) 30%, transparent);
    }
    /* In-flow card (escalation form lives inside the conversation, scrolling
       with messages) — overrides the .order-form panel-section styling. */
    .esc-form {
      border: 1px solid var(--border);
      border-radius: 18px;
      box-shadow: 0 2px 8px color-mix(in srgb, var(--primary) 6%, transparent);
      margin-top: 8px;
      max-height: none;
      overflow: visible;
    }
    .esc-form .esc-icon {
      width: 36px;
      height: 36px;
      border-radius: 11px;
      background: var(--accent-soft);
      color: var(--accent-strong);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
    }
    .esc-form .esc-icon svg {
      width: 21px;
      height: 21px;
    }
    .esc-form .order-form-actions {
      justify-content: stretch;
    }
    .esc-form .order-form-actions .submit {
      flex: 1;
    }
    .order-form-actions button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    footer {
      flex-shrink: 0;
      padding: 12px 14px;
      border-top: 1px solid var(--border);
      background: var(--surface);
      /* Stays bright above the confirm scrim (z-index 1) — see header note. */
      position: relative;
      z-index: 2;
    }
    .input-row {
      display: flex;
      align-items: center;
      gap: 9px;
    }
    /* Gradient-border wrapper: subtle on idle, animated brand gradient on focus
       (same shimmer as the floating island). */
    .input-wrap {
      flex: 1;
      min-width: 0;
      border-radius: 999px;
      padding: 2px;
      background: var(--border);
      transition: background 0.2s;
    }
    .input-wrap:focus-within {
      background: linear-gradient(110deg, var(--accent), var(--primary), var(--accent));
      background-size: 220% 100%;
      animation: island-shimmer 3.2s linear infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .input-wrap:focus-within {
        animation: none;
      }
    }
    .input {
      width: 100%;
      height: 40px;
      padding: 0 15px;
      border-radius: 999px;
      border: none;
      background: var(--surface-2);
      color: var(--ink);
      font-size: 14px;
      font-family: inherit;
      outline: none;
      box-sizing: border-box;
    }
    .send-btn {
      width: 44px;
      height: 44px;
      flex-shrink: 0;
      background: var(--accent-strong);
      color: var(--accent-ink);
      border: none;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 4px 12px color-mix(in srgb, var(--accent) 30%, transparent);
      transition: background 0.15s, opacity 0.15s;
    }
    .send-btn svg {
      width: 18px;
      height: 18px;
    }
    .send-btn:hover:not(:disabled) {
      background: var(--accent-hover);
    }
    .send-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Stały pasek prawny — wydzielona OSTATNIA sekcja panelu, pod inputem.
       Oznaczenie AI + link do warunków muszą być widoczne przez CAŁĄ rozmowę
       (art. 50 AI Act; dawny welcome-note znikał po pierwszej wiadomości),
       a nad inputem miejsce zajmują cart-bar / link do koszyka. */
    .panel-legal {
      flex-shrink: 0;
      font-size: 11px;
      color: var(--ink-muted);
      padding: 6px 14px 8px;
      border-top: 1px solid var(--border);
      background: var(--surface-2);
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 5px;
      flex-wrap: wrap;
      text-align: center;
      /* Jak footer: nad scrimem confirm (z-index 1). */
      position: relative;
      z-index: 2;
    }
    .powered-by {
      color: var(--ink-muted);
    }
    .footer-sep {
      color: var(--ink-muted);
      opacity: 0.6;
    }
    .cookie-reopen {
      background: none;
      border: none;
      color: var(--ink-muted);
      font-size: 11px;
      padding: 0;
      cursor: pointer;
      font-family: inherit;
    }
    .cookie-reopen:hover {
      color: var(--ink);
    }

    /* Cookie info panel — informational, opened via footer link.
       Flex column: fixed title + scrollable sections + pinned footer so the
       Close button is always reachable at the bottom, however long the text. */
    .cookie-banner {
      /* Full-panel overlay (inset:0) → the flex column has a DEFINITE height, so
         the scrollable middle sizes + scrolls reliably on every screen. A
         max-height:% off a panel whose height isn't definite (mobile) collapsed
         the card: content squished, top clipped. Panel's overflow:hidden +
         border-radius clip this to the rounded corners. */
      position: absolute;
      inset: 0;
      background: var(--surface);
      box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.08);
      z-index: 5;
      display: flex;
      flex-direction: column;
    }
    .cookie-title {
      flex-shrink: 0;
      margin: 0;
      padding: 16px 16px 10px 16px;
      font-size: 14px;
      font-weight: 600;
      color: var(--ink);
    }
    .cookie-scroll {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      padding: 0 16px 4px 16px;
    }
    .cookie-section-title {
      margin: 14px 0 4px 0;
      font-size: 13px;
      font-weight: 600;
      color: var(--ink);
    }
    .cookie-scroll > .cookie-section-title:first-child {
      margin-top: 0;
    }
    .cookie-body {
      margin: 0 0 8px 0;
      font-size: 12px;
      line-height: 1.5;
      color: var(--ink-muted);
    }
    .cookie-actions {
      flex-shrink: 0;
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      padding: 12px 16px;
      border-top: 1px solid var(--border);
      background: var(--surface);
    }
    .cookie-btn {
      font-family: inherit;
      font-size: 13px;
      padding: 8px 12px;
      border-radius: 8px;
      border: 1px solid transparent;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s;
    }
    .cookie-btn-primary {
      background: var(--accent);
      color: var(--accent-ink);
      border-color: var(--accent);
    }
    .cookie-btn-primary:hover {
      background: var(--accent-hover);
    }

    /* ============= In-widget confirm modal (e.g. "New conversation?") ============= */
    .confirm-scrim {
      position: absolute;
      inset: 0;
      /* Below the header/footer (z-index 2) so only the conversation area dims,
         but above the message body (z-index 0). */
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      background: color-mix(in srgb, var(--primary) 38%, transparent);
      animation: confirmScrimIn 0.16s ease;
    }
    .confirm-modal {
      width: 100%;
      max-width: 320px;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 18px;
      padding: 20px;
      box-shadow: 0 18px 48px color-mix(in srgb, var(--primary) 24%, transparent);
      animation: confirmModalIn 0.18s cubic-bezier(0.2, 0.8, 0.2, 1);
    }
    .confirm-title {
      margin: 0 0 8px 0;
      font-size: 15px;
      font-weight: 700;
      color: var(--ink);
    }
    .confirm-body {
      margin: 0 0 18px 0;
      font-size: 13px;
      line-height: 1.5;
      color: var(--ink-muted);
    }
    .confirm-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
    }
    .confirm-btn {
      font-family: inherit;
      font-size: 13px;
      font-weight: 600;
      padding: 9px 16px;
      border-radius: 10px;
      border: 1px solid transparent;
      cursor: pointer;
      transition: opacity 0.15s, background 0.15s, border-color 0.15s;
    }
    .confirm-cancel {
      background: var(--secondary);
      color: var(--secondary-ink);
      border-color: var(--secondary-border);
    }
    .confirm-cancel:hover {
      background: var(--secondary-hover);
    }
    .confirm-ok {
      background: var(--accent-strong);
      color: var(--accent-ink);
      box-shadow: 0 4px 12px color-mix(in srgb, var(--accent) 30%, transparent);
    }
    .confirm-ok:hover {
      opacity: 0.92;
    }
    @keyframes confirmScrimIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes confirmModalIn {
      from { opacity: 0; transform: translateY(8px) scale(0.97); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    @media (prefers-reduced-motion: reduce) {
      .confirm-scrim, .confirm-modal, .cart-sheet, .cart-scrim, .cart-steps { transition: none; animation: none; }
    }

    /* ============= Mobile peek bottom-sheet (auto-shown on mount) ============= */
    .peek-sheet {
      position: fixed;
      left: 0;
      right: 0;
      /* CELOWO bez launcher_offset_mobile i safe-area: sheet z natury dochodzi
         do fizycznego dołu ekranu (padding kryje home-indicator); offset tenanta
         podnosi tylko launcher/island, uniesiony sheet wyglądałby na oderwany. */
      bottom: 0;
      background: var(--surface);
      border-radius: 20px 20px 0 0;
      padding: 28px 22px 18px;
      box-shadow: 0 -10px 36px rgba(0, 0, 0, 0.16);
      animation: peek-slide-up 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
      box-sizing: border-box;
    }
    @keyframes peek-slide-up {
      from { transform: translateY(100%); opacity: 0.4; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    /* Shared round close (X) — the peek sheet and the nudge bubble differ only in size/offset. */
    .x-close {
      position: absolute;
      background: rgba(31, 27, 38, 0.06);
      border: none;
      padding: 0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: var(--ink-muted);
      transition: background 0.15s;
    }
    .x-close:hover, .x-close:active {
      background: rgba(31, 27, 38, 0.12);
      color: var(--ink);
    }
    .peek-close {
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
      font-size: 14px;
    }
    .peek-greeting {
      margin: 0 0 18px;
      font-size: 18px;
      font-weight: 600;
      color: var(--ink);
      line-height: 1.35;
      padding-right: 36px;
    }
    .peek-cta {
      width: 100%;
      background: var(--primary);
      color: var(--primary-ink);
      border: none;
      border-radius: 14px;
      padding: 15px 16px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s, transform 0.1s;
      font-family: inherit;
    }
    .peek-cta:hover { background: var(--primary-dark); }
    .peek-cta:active { transform: scale(0.985); }
    .peek-powered {
      text-align: center;
      font-size: 11px;
      color: var(--ink-muted);
      margin-top: 14px;
      opacity: 0.7;
    }
    /* Brand logo — exact kobot.chat colors on light; on dark the "kobot" wordmark
       flips to white via --logo-ink (".chat" badge stays orange on both). */
    .powered-link {
      display: inline-flex;
      align-items: center;
      line-height: 0;
      text-decoration: none;
      transition: opacity 0.15s;
    }
    .powered-link:hover {
      opacity: 0.7;
    }
    .powered-logo {
      height: 18px;
      width: auto;
      display: inline-block;
      vertical-align: middle;
      position: relative;
      top: 2px;
    }
    :host([data-theme='dark']) .powered-logo {
      --logo-ink: #fff;
    }
    /* Warstwa "kobot" logotypu — celowo w arkuszu (adoptedStyleSheets), nie w
       atrybucie style: CSP hosta (style-src bez 'unsafe-inline') blokuje style
       inline i logo czerniało. Arkusz nadpisuje fallbackowy atrybut fill. */
    .powered-logo .logo-ink {
      fill: var(--logo-ink, #331c39);
    }

    /* ============= Mobile fullscreen takeover ============= */
    @media (max-width: 640px) {
      /* safe-area = home-indicator iPhone'ów; --launcher-mobile-offset = per-tenant
         podniesienie przycisku (ui_theme.launcher_offset_mobile — strony z dolnym
         menu barem zasłaniały launcher). Desktop zostaje na bazowych 24px. */
      :host {
        bottom: calc(16px + env(safe-area-inset-bottom, 0px) + var(--launcher-mobile-offset, 0px));
        right: 16px;
      }
      :host([data-position='left']) {
        right: auto;
        left: 16px;
      }
      .launcher {
        width: 52px;
        height: 52px;
      }
      .island {
        /* match the mobile launcher offset (incl. safe-area + per-tenant lift) */
        bottom: calc(16px + env(safe-area-inset-bottom, 0px) + var(--launcher-mobile-offset, 0px));
      }
      .panel {
        position: fixed;
        /* inset:0 alone stretches to the layout viewport — no 100vw/100dvh.
           dvh re-resolves while the browser chrome (URL bar) collapses on
           scroll, which resized/jittered the panel on long pages (2026-06-09).
           Body scroll-lock (lockBodyScroll) stops the page moving underneath. */
        inset: 0;
        width: auto;
        height: auto;
        max-width: none;
        max-height: none;
        border-radius: 0;
        border: none;
        animation: panel-fade-in 200ms ease-out;
      }
      @keyframes panel-fade-in {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      /* Mobile = the same widget, fullscreen (mockup parity). Only safe-area +
         iOS zoom-prevention differ; header/bubbles/input keep their base look.
         Safe-area siedzi na pasku prawnym — to on jest teraz dolną krawędzią. */
      .panel-legal {
        padding-bottom: calc(8px + env(safe-area-inset-bottom));
      }
      .input {
        font-size: 16px; /* >=16px prevents iOS zoom on focus */
      }
      /* Order-form fields are 13/13.5px on desktop; bump to 16px on mobile so
         iOS Safari doesn't zoom the chat when one gets focus. */
      .order-form input,
      .order-form textarea,
      .order-form .phone-cc,
      .pvselect {
        font-size: 16px;
      }
      /* No scrollbar on mobile — touch scroll, no thumb to drag. */
      .scroll-area {
        scrollbar-width: none;
      }
      .scroll-area::-webkit-scrollbar {
        display: none;
      }
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    // Demo mode (panel Appearance preview): render the real widget panel with an
    // in-memory config, no backend. Skip the widget-key requirement, WidgetClient/
    // WidgetSession, bootstrap, instance registration and the time ticker.
    if (this.demo) {
      this.bootDemo();
      return;
    }
    if (!this.widgetKey) {
      console.error(`[${BRAND_NAME}] Missing widget-key attribute`);
      return;
    }
    const base = this.apiBase || `${window.location.origin}/api/v1/widget`;
    this.client = new WidgetClient(base, this.widgetKey);
    this.session = new WidgetSession(this.widgetKey);
    this.locale = resolveLocale(this.localeAttr || null);
    // Eager bootstrap żeby zweryfikować visibility (widget_enabled +
    // hidden_url_patterns) PRZED otwarciem czatu — jeśli owner ukrył widget,
    // launcher znika zamiast czekać na pierwsze kliknięcie. Failsafe: błąd
    // bootstrap nie ukrywa widgetu, traktujemy jak "default visible".
    void this.ensureBooted().then(() => {
      // Restore open state across navigation/refresh: if the visitor had the
      // chat open on the previous page, reopen it (which restores the active
      // conversation + history). Skip when the owner hid the widget here.
      if (this.session?.isPanelOpen() && !this.isHidden) {
        void this.openPanel();
      }
      this.scheduleTeaser();
    });
    registerWidgetInstance(this);
    // Age the relative timestamps in place — only re-render while the panel is
    // open (closed → nothing to update). setInterval, not a render-time clock,
    // so "przed chwilą" → "minutę temu" without the user touching anything.
    this.timeTicker = setInterval(() => {
      // Only re-render to age timestamps if a message actually carries one (skip
      // greeting-only / pre-reply panels — nothing to age).
      if (this.isOpen && this.messages.some((m) => m.created_at)) this.requestUpdate();
    }, 60_000);
  }

  /**
   * Boot the panel Appearance preview: build an in-memory config from the
   * demo-config + discrete theme attributes, seed a 3-line sample transcript and
   * open the panel — all without any backend (no WidgetClient / WidgetSession /
   * bootstrap / instance registration / time ticker). applyDemoTheme() reuses the
   * exact same applyTheme() the live widget runs, so the ink-contrast vars compute
   * here too and the preview is pixel-faithful to production.
   */
  private bootDemo(): void {
    const demo = this.parseDemoConfig();
    const botName = demo.bot_name?.trim() || 'Bot';
    const greeting = demo.greeting ?? null;

    this.locale = resolveLocale(this.localeAttr || null);
    this.config = {
      tenant_slug: 'demo',
      session_token: '',
      session_ttl_seconds: 0,
      locale: this.locale,
      i18n: { ...FALLBACK_I18N, ...(demo.i18n ?? {}) },
      bot: {
        name: botName,
        avatar_url: demo.avatar_url ?? null,
        greeting,
        suggested_questions: [],
      },
      theme: {},
      branding: { show: false },
      features: {},
      visibility: { enabled: true, hidden_url_patterns: [] },
      widgets: {},
    };

    this.applyDemoTheme();
    this.messages = this.seedDemoTranscript(demo);

    // Show the OPEN panel and skip launcher/peek/island branches in render().
    this.isOpen = true;
    this.bootstrapAttempted = true;
  }

  /**
   * Deterministic sample transcript for the panel preview: greeting (if any) +
   * one user line. Pure of `this` so bootDemo() and applyDemoConfig() seed
   * identically.
   */
  private seedDemoTranscript(demo: DemoConfig): ChatMessage[] {
    const seeded: ChatMessage[] = [];
    if (demo.greeting) seeded.push({ id: 'demo-greeting', role: 'assistant', content: demo.greeting });
    if (demo.sample_user_msg) seeded.push({ id: 'demo-user', role: 'user', content: demo.sample_user_msg });
    return seeded;
  }

  /**
   * Public (panel-facing) — re-read the demo-config attribute after a live edit
   * (bot name / greeting / unsaved avatar) and reflect it WITHOUT a re-boot. The
   * widget otherwise parses demo-config only in connectedCallback/bootDemo, so the
   * panel's setAttribute('demo-config', …) alone would never show. No-op outside
   * demo. Theme stays on applyDemoTheme().
   */
  applyDemoConfig(): void {
    if (!this.demo || this.config === null) return;
    const demo = this.parseDemoConfig();
    this.config = {
      ...this.config,
      bot: {
        ...this.config.bot,
        name: demo.bot_name?.trim() || this.config.bot.name,
        avatar_url: demo.avatar_url ?? null,
        greeting: demo.greeting ?? null,
      },
    };
    this.messages = this.seedDemoTranscript(demo);
    this.requestUpdate();
  }

  /** Parse the demo-config JSON attribute; malformed → empty config (defaults). */
  private parseDemoConfig(): DemoConfig {
    if (!this.demoConfigAttr) return {};
    try {
      const parsed = JSON.parse(this.demoConfigAttr) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as DemoConfig) : {};
    } catch {
      return {};
    }
  }

  /**
   * Public (panel-facing) — read the discrete *-color / color-scheme /
   * widget-position / entry-style attributes into a WidgetTheme and run the
   * EXISTING applyTheme(). The panel calls this on each live edit. entry_style is
   * forced to 'bubble' (island makes .panel position:fixed → breaks containment).
   * Reuses the full color-mix palette + auto-contrast ink — nothing re-implemented.
   */
  applyDemoTheme(): void {
    const scheme = this.colorSchemeAttr === 'dark' ? 'dark' : 'light';
    const position = this.widgetPositionAttr === 'left' ? 'left' : 'right';
    this.applyTheme({
      primary_color: this.primaryColorAttr || null,
      accent_color: this.accentColorAttr || null,
      primary_ink: this.primaryInkAttr || null,
      accent_ink: this.accentInkAttr || null,
      color_scheme: scheme,
      widget_position: position,
      entry_style: 'bubble',
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.clearPeekAutoTimeout();
    this.clearNudgeTimeout();
    if (this.timeTicker !== undefined) {
      clearInterval(this.timeTicker);
      this.timeTicker = undefined;
    }
    if (this.cartRedirectTimer !== null) {
      clearTimeout(this.cartRedirectTimer);
      this.cartRedirectTimer = null;
    }
    this.unlockBodyScroll(); // never leave the host page frozen
    unregisterWidgetInstance(this);
  }

  /**
   * Mobile auto-peek: ~1.5s after a SUCCESSFUL boot the bottom-sheet slides up.
   * Only when viewport <=640px, once per session (sessionStorage flag per
   * widget_key), skipped if the user already opened the chat. Called from the
   * boot success path, so the config (real greeting + bot name) is already loaded
   * and a failed/blocked bootstrap never teases.
   */
  private schedulePeekAutoOpen(): void {
    if (typeof window === 'undefined') return;
    const isMobile = window.matchMedia(MOBILE_BREAKPOINT).matches;
    if (!isMobile) return;

    if (this.isPeekDismissed()) return;

    this.peekAutoTimeoutId = window.setTimeout(() => {
      this.peekAutoTimeoutId = null;
      if (this.isOpen || this.bootError !== null) return;
      this.peekVisible = true;
    }, 1500);
  }

  private clearPeekAutoTimeout(): void {
    if (this.peekAutoTimeoutId !== null) {
      clearTimeout(this.peekAutoTimeoutId);
      this.peekAutoTimeoutId = null;
    }
  }

  private isPeekDismissed(): boolean {
    return this.session?.isPeekDismissed() ?? false;
  }

  private dismissPeek(): void {
    this.peekVisible = false;
    this.clearPeekAutoTimeout();
    this.session?.markPeekDismissed();
  }

  private openFromPeek(): void {
    this.peekVisible = false;
    this.clearPeekAutoTimeout();
    void this.openPanel();
  }

  /**
   * After boot (success OR failure — the timers re-check bootError themselves):
   * a host nudge outranks the generic mobile peek. Any nudge queued before boot
   * (pre-load `window.<Brand>Widget.nudge` or a buffered showNudge() — both
   * arrive through showNudge(), see index.ts installNudgeAccessor) is already
   * in this.nudge; a dismissed one still counts as "the visitor closed a teaser
   * this session", so the peek stays off too.
   */
  private scheduleTeaser(): void {
    if (this.nudge !== null) {
      this.scheduleNudge();
      return;
    }
    // Tease the mobile peek only after a SUCCESSFUL boot — a failed bootstrap
    // (origin not on the allowlist, bad/disabled key) must NOT surface the
    // drawer, which would show a greeting for a bot that can't reply.
    if (this.config !== null && this.bootError === null && !this.isHidden) {
      this.schedulePeekAutoOpen();
    }
  }

  /**
   * Host-facing: show a proactive bubble above the launcher. Last call wins
   * (previous timer/bubble dropped); a nudge outranks the generic mobile peek.
   * Show conditions are evaluated when the delay elapses — see scheduleNudge().
   */
  showNudge(options: KobotNudgeOptions): void {
    if (this.demo) return;
    const spec = normalizeNudge(options);
    if (spec === null) return;
    this.clearNudgeTimeout();
    this.nudgeVisible = false;
    this.peekVisible = false;
    this.clearPeekAutoTimeout();
    this.nudge = spec;
    if (this.bootstrapAttempted) this.scheduleNudge();
  }

  /** Idempotent: always drops the previous timer first (no orphans). */
  private scheduleNudge(): void {
    this.clearNudgeTimeout();
    const spec = this.nudge;
    if (spec === null || typeof window === 'undefined') return;
    this.nudgeTimeoutId = window.setTimeout(() => {
      this.nudgeTimeoutId = null;
      if (this.nudge !== spec) return; // replaced meanwhile
      if (this.isOpen || this.isHidden || this.config === null || this.bootError !== null) return;
      if (!this.launcherVisibleForViewport()) return; // launcher_display=hidden → no anchor
      if (this.session?.isNudgeDismissed(spec.id)) return;
      this.nudgeVisible = true;
    }, spec.delay);
  }

  private clearNudgeTimeout(): void {
    if (this.nudgeTimeoutId !== null) {
      clearTimeout(this.nudgeTimeoutId);
      this.nudgeTimeoutId = null;
    }
  }

  /** X, or any other way into the chat while the bubble shows: gone for this session. */
  private dismissNudge(): void {
    const spec = this.nudge;
    this.clearNudgeTimeout();
    this.nudgeVisible = false;
    this.nudge = null;
    if (spec !== null) this.session?.markNudgeDismissed(spec.id);
  }

  /** Bubble body click: open the chat; `query` goes out as the VISITOR's message. */
  private openFromNudge(): void {
    const spec = this.nudge;
    if (spec === null) return;
    this.dismissNudge();
    void this.open({ message: spec.query ?? undefined, source: 'nudge' }); // planOpen: no message = just open
  }

  /** Floating Island submit — opens the (centered) chat and sends the typed question. */
  private openFromIsland(): void {
    if (this.demo) return;
    const text = this.islandValue.trim();
    if (text === '') return;
    this.islandValue = '';
    void this.openPanel().then(() => {
      void this.send(text);
    });
  }

  private renderIsland() {
    return html`
      <form
        class="island"
        @submit=${(e: SubmitEvent) => {
          e.preventDefault();
          this.openFromIsland();
        }}
      >
        ${this.nudgeVisible ? this.renderNudge('island') : nothing}
        <div class="island-inner">
          <input
            class="island-input"
            type="text"
            placeholder=${this.t.islandPlaceholder}
            maxlength="1000"
            .value=${this.islandValue}
            @input=${(e: Event) => (this.islandValue = (e.target as HTMLInputElement).value)}
            aria-label=${this.t.openChat}
          />
          <button class="island-send" type="submit" ?disabled=${!this.islandValue.trim()}>
            ${this.t.send}
          </button>
        </div>
      </form>
    `;
  }

  /** Current locale strings — server-provided per tenant (bootstrap i18n). The
   * minimal fallback only applies on the bootstrap-failure render path. */
  private get t() {
    return this.config?.i18n ?? FALLBACK_I18N;
  }

  private async ensureBooted(): Promise<void> {
    if (this.config !== null || !this.client) {
      this.bootstrapAttempted = true;
      return;
    }
    try {
      this.config = await this.client.bootstrap();
      this.bootError = null; // clear a stale failure on a successful retry
      // UI default language = tenant config (from bootstrap), unless the host set an
      // explicit `locale` attribute (which wins). The bot still auto-adapts to the
      // customer's written language independently.
      if (!this.localeAttr) this.locale = resolveLocale(this.config.locale);
      // Domyślny kierunkowy w formularzach = język bota z konfiguracji (pl→+48,
      // de→+49…), niezależnie od <html lang> hosta / języka przeglądarki.
      this.seedDialCodeDefaults();
      this.applyTheme(this.config.theme);
      this.productDisplayMode = this.config.widgets?.product_display_mode === 'compact' ? 'compact' : 'card';
      this.variantSelectorStyle = this.config.widgets?.variant_selector === 'dropdown' ? 'dropdown' : 'buttons';
      // Product image fit (panel „Widgety") → CSS var consumed by .pcard/.ptile imgs.
      this.style.setProperty(
        '--product-img-fit',
        this.config.widgets?.product_image_fit === 'contain' ? 'contain' : 'cover',
      );
      this.applyVisibility(this.config);
      // <brand>-search (Task 2) is a separate element that may live anywhere on
      // the host page — publish the theme accent + strings it needs right after
      // a successful bootstrap so it can render without its own API call.
      // Ink = the same resolveInk() rule applyTheme() uses for --primary-ink.
      const primary = this.config.theme.primary_color;
      const accentInk = resolveInk(this.config.theme.primary_ink, primary);
      publishSearchContext({
        accent: primary ?? '',
        accentInk,
        placeholder: this.t.searchPlaceholder ?? '',
        buttonLabel: this.t.searchButton ?? '',
      });
    } catch (err) {
      this.bootError = this.formatError(err);
    } finally {
      this.bootstrapAttempted = true;
    }
  }

  /**
   * Stosuje visibility: widget_enabled (globalna flaga) i hidden_url_patterns
   * (URL whitelist). Brak `visibility` w response = backwards compat = visible.
   */
  private applyVisibility(config: WidgetConfig): void {
    const vis = config.visibility;
    if (!vis) {
      this.disabledByOwner = false;
      this.hiddenByUrlPattern = false;
      return;
    }
    if (!vis.enabled) {
      this.disabledByOwner = true;
      this.hiddenByUrlPattern = false;
      return;
    }
    this.disabledByOwner = false;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    this.hiddenByUrlPattern = (vis.hidden_url_patterns ?? []).some((p) => matchUrlPattern(p, path));
  }

  /**
   * Apply tenant theme — maps tenant_configurations.ui_theme JSON
   * (primary_color, accent_color) to CSS custom properties on :host.
   */
  private applyTheme(theme: WidgetTheme): void {
    const primary = theme.primary_color;
    const accent = theme.accent_color;
    if (primary) {
      this.style.setProperty('--primary', primary);
    }
    if (accent) {
      this.style.setProperty('--accent', accent);
    }

    // Ink (text on the primary/accent surface): the owner's explicit value wins
    // when it's a valid hex; otherwise fall back to auto-contrast (inkFor flips
    // black/white by luminance so light/odd brand colours stay readable).
    const pInk = resolveInk(theme.primary_ink, primary);
    if (pInk) {
      this.style.setProperty('--primary-ink', pInk);
    }
    // --accent-strong is derived darker than --accent, so the accent ink is a
    // safe choice for both --accent and --accent-strong surfaces.
    const aInk = resolveInk(theme.accent_ink, accent);
    if (aInk) {
      this.style.setProperty('--accent-ink', aInk);
    }

    // widget_position ('left' | 'right') → host data attr który CSS reaktywnie
    // zamienia bottom-right ↔ bottom-left. Default right (brak attr).
    if (theme.widget_position === 'left') {
      this.dataset.position = 'left';
    } else {
      delete this.dataset.position;
    }

    // color_scheme ('light' | 'dark'). Flag + [data-theme] attr; the dark
    // stylesheet is adopted/removed conditionally in updated() so light-mode
    // widgets never carry the dark rules.
    this.dark = theme.color_scheme === 'dark';
    if (this.dark) {
      this.dataset.theme = 'dark';
    } else {
      delete this.dataset.theme;
    }

    // Mobilny odstęp od dołu (px) — CSS var czytana w mobile media query przez
    // :host i .island. Clamp 0-200 mimo walidacji panelu i kontrolera
    // (defence-in-depth: theme idzie z DB przez bootstrap).
    const rawOffset = theme.launcher_offset_mobile;
    const offset =
      typeof rawOffset === 'number' && Number.isFinite(rawOffset)
        ? Math.min(200, Math.max(0, Math.round(rawOffset)))
        : 0;
    if (offset > 0) {
      this.style.setProperty('--launcher-mobile-offset', `${offset}px`);
    } else {
      this.style.removeProperty('--launcher-mobile-offset');
    }

    // entry_style ('bubble' | 'island'). Island → chat panel opens centered
    // (CSS keys off [data-layout='island']); the entry UI itself is chosen in render().
    if (theme.entry_style === 'island') {
      this.dataset.layout = 'island';
    } else {
      delete this.dataset.layout;
    }
  }

  /** Conditionally adopt/remove the separate dark stylesheet (see darkStyles). */
  private syncDarkStylesheet(): void {
    const root = this.shadowRoot;
    const sheet = darkStyles.styleSheet;
    if (!root || !sheet) return;

    const adopted = root.adoptedStyleSheets.includes(sheet);
    if (this.dark && !adopted) {
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    } else if (!this.dark && adopted) {
      root.adoptedStyleSheets = root.adoptedStyleSheets.filter((s) => s !== sheet);
    }
  }

  /**
   * Launcher visibility for the current viewport (launcher_display:
   * both|desktop|mobile|hidden). Governs both the bubble and the mobile peek.
   */
  private launcherVisibleForViewport(): boolean {
    const mode = this.config?.theme.launcher_display ?? 'both';
    if (mode === 'hidden') return false;
    if (mode === 'both') return true;
    if (typeof window === 'undefined') return true;
    const isMobile = window.matchMedia(MOBILE_BREAKPOINT).matches;
    return mode === 'mobile' ? isMobile : !isMobile;
  }

  /**
   * Host-facing: open the chat. Optionally seed a question (autoSend defaults
   * to true). No-op if the owner globally disabled the widget; overrides a
   * URL-pattern hide because an explicit host call is a stronger signal.
   */
  async open(options: KobotOpenOptions = {}): Promise<void> {
    // Overwrite (not merge) — an open() with no source clears any stale one from
    // a previous call, so it never leaks into an unrelated later conversation.
    this.pendingSource = options.source ?? null;
    await this.ensureBooted(); // ensure visibility flags are set before the no-op check
    const plan = planOpen(
      { disabledByOwner: this.disabledByOwner, hiddenByUrlPattern: this.hiddenByUrlPattern },
      options,
    );
    if (plan.kind === 'skip') return;
    if (plan.forceVisible) this.hiddenByUrlPattern = false;
    await this.openPanel();
    if (plan.send !== undefined) {
      await this.send(plan.send);
    } else if (plan.prefill !== undefined) {
      this.inputValue = plan.prefill;
    }
  }

  /** Host-facing: close the chat. */
  close(): void {
    this.closePanel();
  }

  private async openPanel(): Promise<void> {
    this.isOpen = true;
    this.nextScroll = 'bottom'; // on (re)open, show the latest of the restored thread
    this.session?.setPanelOpen(true);
    this.peekVisible = false;
    this.clearPeekAutoTimeout();
    // Entered the chat by other means while the bubble showed — it served its purpose.
    if (this.nudgeVisible) this.dismissNudge();
    this.lastError = null;
    this.lockBodyScroll();

    // Bootstrap is needed to get the session token and config. Usually already
    // done in connectedCallback; only retry here if that first attempt failed.
    if (!this.config) {
      await this.ensureBooted();
    }

    if (this.config && !this.conversationId && this.client) {
      const restored = await this.tryRestoreConversation();
      if (!restored) {
        this.showLocalGreeting();
      }
    }
  }

  private openCookieInfo(): void {
    this.cookieInfoOpen = true;
  }

  private closeCookieInfo(): void {
    this.cookieInfoOpen = false;
  }

  /**
   * Próbuje odtworzyć sesję z localStorage. Zwraca true gdy się udało
   * (conversationId set, messages hydrated). False gdy brak sesji /
   * wygasła / closed na backendzie.
   */
  private async tryRestoreConversation(): Promise<boolean> {
    if (!this.client || !this.session) return false;

    const storedId = this.session.getConversationId();
    if (storedId === null) return false;

    try {
      const conv = await this.client.getConversation(storedId);
      if (conv.status !== 'active') {
        this.session.clear();
        return false;
      }
      this.conversationId = conv.conversation_id;
      this.messages = conv.messages.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        products: m.products ?? [],
        cartOps: m.cart_ops ?? [],
        created_at: m.created_at,
      }));
      this.order = conv.order ?? null;
      this.applyEscalationSignal(conv.escalation);
      this.applyContactSignal(conv.contact);
      // Pending ops z historii (np. nawigacja tuż po turze): wykonaj raz —
      // lokalny once-guard + status serwera chronią przed podwójnym dodaniem.
      for (const m of conv.messages) {
        this.runPendingCartOps(m.id, m.cart_ops);
      }
      // Refresh sliding TTL — user wrócił do rozmowy.
      this.session.saveConversationId(conv.conversation_id);
      return true;
    } catch {
      // 404 conversation_not_found → backend wyczyścił, restart fresh.
      // Inne błędy (sieć, 5xx) → też fall through do fresh start;
      // restore best-effort, nie blokujemy chat'a gdy zawiedzie.
      this.session.clear();
      return false;
    }
  }

  /**
   * Pokazuje greeting bota lokalnie — bez backend roundtrip. Conversation
   * NIE jest tworzona (per Mariusz 2026-05-20): otwarcie widget'a samo
   * w sobie nie zlicza się jako czat, dopiero pierwsza wysłana wiadomość.
   */
  private showLocalGreeting(): void {
    if (!this.config?.bot.greeting) return;
    this.messages = [{
      id: 'greeting',
      role: 'assistant',
      content: this.config.bot.greeting,
    }];
  }

  /**
   * Lazy create — wywoływane tuż przed wysłaniem pierwszej wiadomości.
   * Zwraca true gdy mamy działający conversationId (już był albo właśnie
   * utworzony), false gdy backend odmówił (limit / błąd sieci) — wtedy
   * `this.lastError` jest ustawiony i caller powinien przerwać send.
   */
  private async ensureConversation(): Promise<boolean> {
    // A conversation already exists (fresh in-session start OR restored from
    // localStorage) — source is per-conversation, fixed at creation, so it
    // simply doesn't apply here. Clear it now (not just "leave it for open()
    // to overwrite next time"): startFreshConversation() ("Nowa rozmowa") nulls
    // conversationId WITHOUT going through open(), so a pendingSource from an
    // earlier open() call would otherwise wrongly attach to that next chat.
    if (this.conversationId) {
      this.pendingSource = null;
      return true;
    }
    if (!this.client || !this.config) return false;
    try {
      // Consume-on-attempt: the source described THIS open() call's intent, and
      // that's used up whether the create below succeeds or not (a retry after
      // failure is a fresh send(), not a continuation of the original open()).
      const source = this.pendingSource ?? undefined;
      this.pendingSource = null;
      const { conversation_id } = await this.client.startConversation(
        window.location.href,
        this.collectCustomerPrefill(),
        source,
      );
      this.conversationId = conversation_id;
      this.session?.saveConversationId(conversation_id);
      return true;
    } catch (err) {
      this.lastError = this.formatError(err);
      return false;
    }
  }

  /**
   * Host-facing: dane zalogowanego klienta sklepu (imię, email, telefon) do
   * prefillu zamówienia — skraca flow składania (bot nie pyta o dane).
   * Działa przed startem rozmowy; po starcie zapamiętane na kolejną.
   */
  setCustomer(data: KobotCustomerData): void {
    this.customerOverride = data;
  }

  /**
   * Priorytet per-pole: setCustomer() → window.<Brand>Widget.customer
   * (pre-load convention — inline skrypt hosta biegnie PRZED modułem) →
   * atrybuty elementu (manualny embed).
   */
  private collectCustomerPrefill(): { name?: string; phone?: string; email?: string } | undefined {
    const globalCustomer = readGlobalCustomer();
    const name = (this.customerOverride?.name ?? globalCustomer?.name ?? this.customerNameAttr).trim();
    const email = (this.customerOverride?.email ?? globalCustomer?.email ?? this.customerEmailAttr).trim();
    const phone = (this.customerOverride?.phone ?? globalCustomer?.phone ?? this.customerPhoneAttr).trim();
    if (!name && !email && !phone) return undefined;
    return {
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
    };
  }

  private closePanel(): void {
    // Demo (panel Appearance preview) — the preview must stay a stable OPEN
    // transcript; the header X is inert (no session/recovery in demo).
    if (this.demo) return;
    this.isOpen = false;
    this.session?.setPanelOpen(false);
    this.unlockBodyScroll();
  }

  /**
   * On MOBILE the panel is fullscreen, so clicking a content link/button that navigates
   * the page same-tab (product "details", product name, a bot-message link) would leave
   * the chat covering the destination after reload — the open flag persists across pages.
   * Close the panel on such a click: closePanel() persists panel_open=0 (so a full reload
   * lands closed) and an SPA route change sees it close at once. We do NOT preventDefault —
   * the navigation proceeds. Desktop is unaffected (the panel doesn't cover the page there).
   */
  private onContentLinkNavigate(e: MouseEvent): void {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const anchor = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (anchor === null) return;
    const href = anchor.getAttribute('href') ?? '';
    // New-tab / non-navigating anchors don't hide the page → leave the chat open.
    if (anchor.target === '_blank' || href === '' || href.startsWith('#') || href.startsWith('javascript:')) return;
    if (window.matchMedia(MOBILE_BREAKPOINT).matches) {
      this.closePanel();
    }
  }

  // ---- Mobile body scroll lock --------------------------------------------
  // Fullscreen panel on mobile: the host page must not scroll underneath —
  // scrolling moved the browser chrome (URL bar) and tore visual "holes" under
  // the widget (issue 2026-06-09). position:fixed on <body> is the iOS-safe
  // technique (overflow:hidden alone is ignored by iOS Safari); the saved
  // scrollY is restored on close so the visitor doesn't lose their place.

  /** scrollY zapisany przy locku; null = lock nieaktywny. */
  private scrollLockY: number | null = null;

  /**
   * Per-property snapshot: [wartość hosta sprzed locka, wartość ustawiona
   * przez NAS]. Przy unlocku przywracamy TYLKO te propsy, które nadal trzymają
   * naszą wartość — jeśli host (modal/cookie-banner) nadpisał props gdy czat
   * był otwarty, zostawiamy jego wartość zamiast klobbrować ją stalym
   * snapshotem (review 2026-06-10).
   */
  private scrollLockSaved: Array<[prop: string, before: string, applied: string]> | null = null;

  private lockBodyScroll(): void {
    if (typeof window === 'undefined' || this.scrollLockY !== null) return;
    if (!window.matchMedia(MOBILE_BREAKPOINT).matches) return;

    const body = document.body;
    this.scrollLockY = window.scrollY;
    const apply: Array<[prop: string, value: string]> = [
      ['position', 'fixed'],
      ['top', `-${this.scrollLockY}px`],
      ['left', '0'],
      ['right', '0'],
      ['width', '100%'],
      ['overflow', 'hidden'],
    ];
    this.scrollLockSaved = apply.map(([prop, value]) => {
      const before = body.style.getPropertyValue(prop);
      body.style.setProperty(prop, value);
      return [prop, before, value];
    });
  }

  private unlockBodyScroll(): void {
    if (this.scrollLockY === null) return;
    const y = this.scrollLockY;
    const saved = this.scrollLockSaved ?? [];
    this.scrollLockY = null;
    this.scrollLockSaved = null;

    const body = document.body;
    let positionRestored = false;
    for (const [prop, before, applied] of saved) {
      // Host zmienił props w międzyczasie → jego wartość wygrywa, nie ruszamy.
      if (body.style.getPropertyValue(prop) !== applied) continue;
      if (before === '') {
        body.style.removeProperty(prop);
      } else {
        body.style.setProperty(prop, before);
      }
      if (prop === 'position') positionRestored = true;
    }
    // Przywróć scroll tylko gdy to MY trzymaliśmy body w position:fixed —
    // inaczej scroll state należy już do hosta.
    if (positionRestored) window.scrollTo(0, y);
  }

  /**
   * "Nowa rozmowa" header button — drops the current conversation_id
   * (server-side it stays closed/active, we just stop talking to it),
   * clears localStorage, and bootstraps a fresh conversation. Confirmation
   * dialog because losing a long thread by accident is annoying.
   */
  private handleStartNewConversation(): void {
    // Demo (panel Appearance preview) — the seeded transcript has no backend and
    // no recovery; the new-conversation button is inert so the preview stays stable.
    if (this.demo) return;
    if (this.isLoading) return;
    // A real thread is at stake → confirm via the in-widget modal. An untouched
    // greeting (≤1 message) resets straight away — nothing to lose.
    if (this.messages.length > 1) {
      this.confirmNewOpen = true;
      return;
    }
    this.startFreshConversation();
  }

  private confirmNewConversation(): void {
    this.confirmNewOpen = false;
    this.startFreshConversation();
  }

  private cancelNewConversation(): void {
    this.confirmNewOpen = false;
  }

  private startFreshConversation(): void {
    this.session?.clear();
    this.conversationId = null;
    // Belt-and-braces alongside ensureConversation()'s clear: send() can
    // early-return (isLoading / bootError / blank input) BEFORE ever calling
    // ensureConversation(), leaving a pendingSource from an earlier
    // open({source}) unconsumed. Without this, that stale source would
    // wrongly attach to the next conversation started from this reset.
    this.pendingSource = null;
    this.messages = [];
    this.order = null;
    this.lastError = null;
    // Drop any open flows so the fresh chat starts clean.
    this.orderFormOpen = false;
    this.cartSheetOpen = false;
    this.escalationActive = false;
    this.escalationAnchorId = null;
    this.contactAnchorId = null;
    this.contactActive = false;
    this.escalationSubmitted = false;
    this.contactSubmitted = false;
    this.contactRequestedAt = null;
    this.escalationRequestedAt = null;
    this.variantSelections = {}; // don't carry variant picks into the new chat
    // Tylko lokalne greeting — backend Conversation powstanie przy pierwszej
    // wiadomości użytkownika (lazy create per Mariusz 2026-05-20).
    this.showLocalGreeting();
  }

  private async send(text: string): Promise<void> {
    // Demo (panel preview) — no backend, sending is disabled.
    if (this.demo) return;
    const trimmed = text.trim();
    // bootError → widget is inoperable (bad key / failed boot); never send.
    if (!trimmed || !this.client || this.isLoading || this.bootError) return;

    this.lastError = null;
    this.inputValue = '';
    this.isLoading = true;

    // Lazy create — dopiero teraz tworzymy Conversation w backendzie. Jeśli
    // backend odmówi (np. limit miesięczny), error banner się pojawi i nie
    // wysyłamy wiadomości. User message dodajemy DOPIERO po sukcesie create
    // żeby nie zostawiać "wiszącej" wiadomości w UI gdy create się wywali.
    const ready = await this.ensureConversation();
    if (!ready) {
      this.isLoading = false;
      return;
    }

    this.messages = [
      ...this.messages,
      { id: `u-${Date.now()}`, role: 'user', content: trimmed },
    ];
    // Pin this question near the top so the reply reads from its start.
    this.nextScroll = 'responseTop';

    try {
      await this.sendStreaming(trimmed);
    } catch (err) {
      const cause = err instanceof StreamFailure ? err.cause : err;
      const turnStarted = err instanceof StreamFailure && err.turnStarted;
      const bannerSet = err instanceof StreamFailure && err.bannerSet;

      // Fallback to sync send only when the stream died BEFORE the server
      // emitted ANY event: endpoint disabled (404), server/provider error
      // (5xx) or network failure (status 0) at that point means the turn
      // never started server-side, so re-sending cannot duplicate it. Once
      // any SSE event arrived the turn is running — even with an empty text
      // buffer (`replace`('') clears it during tool execution) — so we keep
      // the banner instead of re-sending.
      if (!turnStarted && this.isStreamFallbackEligible(cause)) {
        try {
          await this.sendSync(trimmed);
        } catch (syncErr) {
          this.lastError = this.formatError(syncErr);
        }
      } else if (!bannerSet) {
        this.lastError = this.formatError(cause);
      }
    } finally {
      this.isLoading = false;
      // Scroll handled by updated() via autoScroll() — keeps the response pinned
      // to the top rather than jumping to the newest line.
    }
  }

  /** Stream errors eligible for the sync-send fallback (vs banner-only). */
  private isStreamFallbackEligible(cause: unknown): boolean {
    return (
      cause instanceof WidgetApiException &&
      (cause.status === 404 || cause.status === 0 || cause.status >= 500)
    );
  }

  /**
   * Streaming path — append placeholder assistant message, mutate content on
   * each delta. replace/append events redirect content stream. done event
   * finalizes id. On failure the placeholder is removed and the error is
   * rethrown as StreamFailure so send() knows whether any output was shown.
   */
  private async sendStreaming(message: string): Promise<void> {
    if (!this.client || !this.conversationId) return;

    // Placeholder z tymczasowym ID — replace na message_id z `done`.
    const placeholderId = `a-stream-${Date.now()}`;
    let accumContent = '';
    let receivedAnyEvent = false;
    let errorEventSeen = false;
    let finalId: string | null = null;
    let finalProducts: ProductCard[] = [];
    let finalCartOps: CartOp[] = [];
    let finalCreatedAt: string | undefined;

    this.messages = [
      ...this.messages,
      { id: placeholderId, role: 'assistant', content: '' },
    ];

    const updatePlaceholder = (content: string): void => {
      this.messages = this.messages.map((m) =>
        m.id === placeholderId ? { ...m, content } : m,
      );
    };

    try {
      await this.client.streamMessage(this.conversationId, message, (event) => {
        receivedAnyEvent = true;
        switch (event.type) {
          case 'start':
            break;
          case 'delta':
          case 'append':
          case 'tool_result':
            accumContent += event.content;
            updatePlaceholder(accumContent);
            break;
          case 'replace':
            accumContent = event.content;
            updatePlaceholder(accumContent);
            break;
          case 'signals':
            // Wczesna kopia sygnałów (serwer wysyła po dispatchu narzędzi) —
            // formularz pojawia się nawet gdy stream urwie się przed done.
            this.applyEscalationSignal(event.escalation);
            this.applyContactSignal(event.contact);
            break;
          case 'done':
            finalId = event.message_id;
            // Strukturalne dane przychodzą dopiero z `done` (delta niesie tylko
            // tekst). Parytet z sendSync — bez tego widget gubił karty produktów
            // i cart bar, których sandbox zawsze pokazuje.
            finalProducts = event.products ?? [];
            finalCartOps = event.cart_ops ?? [];
            finalCreatedAt = event.created_at;
            if (event.order !== undefined) {
              this.order = event.order;
            }
            this.applyEscalationSignal(event.escalation);
            this.applyContactSignal(event.contact);
            break;
          case 'error':
            this.lastError = event.message;
            errorEventSeen = true;
            break;
        }
      });
    } catch (err) {
      // Pusty placeholder zdejmujemy (wisiał jako pusty dymek; przy fallbacku
      // dublowałby odpowiedź). Częściowy tekst ZOSTAJE — użytkownik go
      // widział, banner wyjaśnia przerwanie.
      if (accumContent === '') {
        this.messages = this.messages.filter((m) => m.id !== placeholderId);
      }
      throw new StreamFailure(err, receivedAnyEvent, errorEventSeen);
    }

    // Stream zakończony bez `done` (in-band {type:'error'} + zamknięcie) —
    // pusty placeholder zdejmujemy, żeby nie wisiał obok banera błędu.
    if (finalId === null && accumContent === '') {
      this.messages = this.messages.filter((m) => m.id !== placeholderId);
    }

    // Replace placeholder id z faktycznym message_id + dołącz produkty (jeśli
    // got done). Spread zachowuje zakumulowaną treść z delt.
    if (finalId !== null) {
      this.messages = this.messages.map((m) =>
        m.id === placeholderId
          ? { ...m, id: finalId!, products: finalProducts, cartOps: finalCartOps, created_at: finalCreatedAt }
          : m,
      );
      // Kotwice formularzy zapalają się w handlerze `done`, czyli ZANIM placeholder
      // dostanie prawdziwe message_id. Bez przeniesienia wskazywałyby na id, którego
      // po tej podmianie nie ma na liście — formularz wracałby na dno kontenera,
      // czyli dokładnie do zachowania, które naprawiamy (a że SSE jest ścieżką
      // domyślną, cały fix byłby martwy poza testami wołającymi sygnał wprost).
      if (this.contactAnchorId === placeholderId) this.contactAnchorId = finalId;
      if (this.escalationAnchorId === placeholderId) this.escalationAnchorId = finalId;
      this.runPendingCartOps(finalId, finalCartOps);
    }
  }

  /**
   * Sync fallback — gdy streaming endpoint zwraca 404 (env disabled).
   */
  private async sendSync(message: string): Promise<void> {
    if (!this.client || !this.conversationId) return;

    const response = await this.client.sendMessage(this.conversationId, message);
    this.messages = [
      ...this.messages,
      {
        id: response.message_id,
        role: 'assistant',
        content: response.content,
        guardrail: response.guardrail_matched,
        products: response.products ?? [],
        cartOps: response.cart_ops ?? [],
        created_at: response.created_at,
      },
    ];
    if (response.order !== undefined) {
      this.order = response.order;
    }
    this.applyEscalationSignal(response.escalation);
    this.applyContactSignal(response.contact);
    this.runPendingCartOps(response.message_id, response.cart_ops);
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      const scroll = this.renderRoot.querySelector<HTMLDivElement>('.scroll-area');
      if (scroll) scroll.scrollTop = scroll.scrollHeight;
    });
  }

  /**
   * Pin the latest visitor message near the top of the thread so the bot's reply
   * reads from its start (the top-aligned avatar marks the beginning) instead of
   * the view jumping to the newest streamed line — the visitor never has to
   * scroll up to find the answer. Falls back to bottom when there is no visitor
   * message yet (e.g. greeting only).
   */
  private scrollResponseToTop(): void {
    requestAnimationFrame(() => {
      const scroll = this.renderRoot.querySelector<HTMLDivElement>('.scroll-area');
      if (!scroll) return;
      const userMsgs = scroll.querySelectorAll<HTMLElement>('.msg.user');
      const last = userMsgs[userMsgs.length - 1];
      if (!last) {
        scroll.scrollTop = scroll.scrollHeight;
        return;
      }
      scroll.scrollTop += last.getBoundingClientRect().top - scroll.getBoundingClientRect().top - 8;
    });
  }

  /** Apply the scroll intent set by the last user action (open vs. send). */
  private autoScroll(): void {
    if (this.nextScroll === 'responseTop') this.scrollResponseToTop();
    else this.scrollToBottom();
  }

  /**
   * Cart variant sub-label. Prefers the structured axes — "Kolor: czerwony,
   * Rozmiar: 10" with axis names localized via variantAxisLabels (same dict the
   * product-card selector uses) — and falls back to the value-only variant_label
   * for items frozen before axes were carried (or external-cart lines).
   */
  private formatVariantLabel(item: OrderItem): string {
    const axes = item.variant_axes;
    if (axes && Object.keys(axes).length > 0) {
      return Object.entries(axes)
        .map(([key, value]) => `${this.t.variantAxisLabels[key] ?? key}: ${value}`)
        .join(', ');
    }
    return item.variant_label ?? '';
  }

  /**
   * Collapsed cart bar (in-flow, above the composer) for a DRAFT cart only. Tap or
   * swipe-up opens the sheet. A submitted order shows no bar — the confirmation lives
   * in the conversation thread (the server's "order placed" + summary message, from
   * both the bot submit and the order form), like an add-to-cart entry.
   */
  private renderCartBar() {
    // External mode: koszyk żyje w SKLEPIE — zamiast builtin cart bara slim link
    // do strony koszyka (czat przeżywa nawigację: stan w local/sessionStorage).
    if (this.externalCartEnabled()) {
      const resolved = resolveShopEndpoint(this.config?.external_cart?.cart_url ?? '', window.location.origin);
      if (resolved === null) return nothing;
      const href = safeUrl(resolved);
      if (href === '') return nothing;
      return html`
        ${this.cartRedirectPending
          ? html`<div class="cart-redirect-note"><span class="cart-op-chip pending">${this.t.externalCartRedirecting}</span></div>`
          : nothing}
        <a class="cart-bar shop-cart-link" href=${href} aria-label=${this.t.openShopCart}>
          <div class="cbi">${ICON_CART}</div>
          <div class="cbt">
            <div class="l1">${this.t.openShopCart}</div>
          </div>
          <span class="chev">${CHEVRON_UP_ICON}</span>
        </a>`;
    }
    if (!this.order || this.order.items.length === 0 || this.order.status !== 'draft') return nothing;
    const totalStr = this.formatCents(this.order.total_cents, this.order.currency) ?? '';
    const itemCount = this.cartItemCount();

    return html`
      <div
        class="cart-bar"
        role="button"
        tabindex="0"
        aria-expanded=${this.cartSheetOpen ? 'true' : 'false'}
        aria-label=${this.t.cartBarTitle}
        @click=${() => this.openCartSheet()}
        @keydown=${this.onCartBarKeydown}
      >
        <div class="cbi">${ICON_CART}</div>
        <div class="cbt">
          <div class="l1">${this.t.cartBarTitle}</div>
          <div class="l2">${itemCount} ${this.t.cartQtyPieces}</div>
        </div>
        <span class="cbv">${totalStr}</span>
        <span class="chev">${CHEVRON_UP_ICON}</span>
      </div>`;
  }

  /** Shared phone field: dial-code <select> + grouped national input (+ a valid-check tick).
   *  Used by both the order form (required) and the escalation form (optional). */
  private renderPhoneRow(opts: {
    cc: string;
    national: string;
    label: string;
    required: boolean;
    valid: boolean;
    disabled: boolean;
    onCc: (e: Event) => void;
    onNational: (e: Event) => void;
  }) {
    const isOther = opts.cc === OTHER_CC;
    return html`
      <div class="phone-row">
        <select class="phone-cc" aria-label=${opts.label} data-cc=${opts.cc}
          @change=${opts.onCc} ?disabled=${opts.disabled}>
          ${PHONE_CODES_SORTED.map((c) => html`<option value=${c.code}>${c.code} ${c.iso}</option>`)}
          <option value=${OTHER_CC}>${this.t.phoneOtherCountry}</option>
        </select>
        <span class="field-input">
          <input type="tel" inputmode="tel" ?required=${opts.required}
            maxlength=${isOther ? '18' : '16'} placeholder=${isOther ? '+…' : ''} .value=${opts.national}
            @input=${opts.onNational} ?disabled=${opts.disabled} />
          ${opts.valid
            ? html`<span class="field-check" aria-hidden="true">${CHECK_ICON}</span>`
            : nothing}
        </span>
      </div>`;
  }

  /**
   * Bottom-sheet overlay (rendered only for a draft order with items). Closed =
   * translateY(100%) off-screen but present in the DOM, so the open-drag can
   * track it and the step-2 form can pre-render for the horizontal slide.
   */
  private renderCartSheet() {
    const order = this.order;
    if (!order || order.items.length === 0 || order.status !== 'draft') return nothing;
    const totalStr = this.formatCents(order.total_cents, order.currency) ?? '';
    const itemCount = this.cartItemCount();
    // Suma SZTUK z jednostką „szt." — nie plural „produktów" (parytet z chipem
    // external cart; „12 produktów" przy 3 pozycjach × 4 szt. mylił).
    const countStr = `${itemCount} ${this.t.cartQtyPieces}`;

    return html`
      <div class="cart-scrim ${this.cartSheetOpen ? 'open' : ''}" @click=${this.closeCartSheet}></div>
      <div
        class="cart-sheet ${this.cartSheetOpen ? 'open' : ''} ${this.orderFormOpen ? 'step2' : ''}"
        role="dialog"
        aria-modal=${this.cartSheetOpen ? 'true' : 'false'}
        aria-label=${this.t.cartTitle}
        tabindex="-1"
        ?inert=${!this.cartSheetOpen}
        ${ref(this.sheetRef)}
        @keydown=${this.onSheetKeydown}
      >
        <div
          class="cart-grip"
          aria-hidden="true"
          @pointerdown=${this.onSheetPointerDown}
          @pointermove=${this.onSheetPointerMove}
          @pointerup=${this.onSheetPointerUp}
          @pointercancel=${this.onSheetPointerCancel}
        ></div>

        <div class="cart-steps">
          <!-- STEP 1 — cart -->
          <div class="cart-step">
            <div class="cart-head">
              <div class="title"><div class="t">${this.t.cartTitle}</div><div class="s">${countStr}</div></div>
              <button class="icon-btn" type="button" @click=${this.closeCartSheet} aria-label=${this.t.closeChat}>${ICON_CLOSE}</button>
            </div>
            <div class="cart-scrollwrap">
              <div class="cart-scroll cart-items" ${ref(this.cartScrollRef)} @scroll=${this.onCartScroll}>
                ${repeat(
                  order.items,
                  (i) => i.product_id + '|' + (i.variant_sku ?? ''),
                  (i) => {
                    const lineStr = this.formatCents(i.line_total_cents, i.currency) ?? '—';
                    const variantText = this.formatVariantLabel(i);
                    return html`
                      <div class="item">
                        <span class="ct">
                          ${ICON_IMG}
                          ${i.image_url
                            ? html`<img src=${safeUrl(i.image_url)} alt="" loading="lazy"
                                @error=${(e: Event) => ((e.target as HTMLImageElement).style.display = 'none')}
                                @load=${(e: Event) => ((e.target as HTMLImageElement).style.display = '')} />`
                            : nothing}
                        </span>
                        <span class="name">
                          <span class="pname">${i.name}</span>
                          ${variantText ? html`<span class="vlabel">${variantText}</span>` : nothing}
                        </span>
                        <span class="qty">× ${i.qty}</span>
                        <span class="line-total">${lineStr}</span>
                      </div>`;
                  },
                )}
              </div>
              <div class="cart-fog" ${ref(this.cartFogRef)}></div>
            </div>
            <div class="cart-foot">
              <div class="ttl"><span class="l">${this.t.cartTotalLabel}</span><span class="v">${totalStr}</span></div>
              <button class="cart-cta" type="button" @click=${this.openOrderForm}>${this.t.cartSubmitButton} ${ICON_SEND}</button>
            </div>
          </div>

          <!-- STEP 2 — order form -->
          <div class="cart-step">
            <div class="cart-head">
              <button class="icon-btn" type="button" @click=${this.backToCart} aria-label=${this.t.cartBack}>${BACK_ICON}</button>
              <div class="title"><div class="t">${this.t.orderFormTitle}</div><div class="s">${countStr} · ${totalStr}</div></div>
              <button class="icon-btn" type="button" @click=${this.closeCartSheet} aria-label=${this.t.closeChat}>${ICON_CLOSE}</button>
            </div>
            <div class="order-form">
              <form @submit=${(e: SubmitEvent) => this.submitOrderForm(e)}>
                <div class="cart-scrollwrap">
                  <div class="cart-scroll order-form-fields" ${ref(this.formScrollRef)} @scroll=${this.onFormScroll}>
                    <label>
                      <span>${this.t.orderFormNameLabel}</span>
                      <input type="text" required maxlength="255" .value=${this.orderFormData.name}
                        @input=${(e: Event) => this.updateOrderFormField('name', (e.target as HTMLInputElement).value)}
                        ?disabled=${this.orderFormSubmitting} />
                      ${this.orderFieldErrors.name !== undefined ? html`<small class="field-error">${this.orderFieldErrors.name}</small>` : nothing}
                    </label>
                    <label>
                      <span>${this.t.orderFormPhoneLabel}</span>
                      ${this.renderPhoneRow({
                        cc: this.orderPhoneCc,
                        national: this.orderFormData.phone,
                        label: this.t.orderFormPhoneLabel,
                        required: true,
                        valid: isValidPhone(this.orderPhoneValue()),
                        disabled: this.orderFormSubmitting,
                        onCc: this.onPhoneCcChange,
                        onNational: this.onPhoneNationalInput,
                      })}
                      ${this.orderFieldErrors.phone !== undefined ? html`<small class="field-error">${this.orderFieldErrors.phone}</small>` : nothing}
                    </label>
                    <label>
                      <span>${this.t.orderFormEmailLabel} <small>${this.t.orderFormEmailRequired}</small></span>
                      <span class="field-input">
                        <input type="email" required maxlength="255" .value=${this.orderFormData.email}
                          @input=${(e: Event) => this.updateOrderFormField('email', (e.target as HTMLInputElement).value)}
                          ?disabled=${this.orderFormSubmitting} />
                        ${isValidEmail(this.orderFormData.email)
                          ? html`<span class="field-check" aria-hidden="true">${CHECK_ICON}</span>`
                          : nothing}
                      </span>
                      ${this.orderFieldErrors.email !== undefined ? html`<small class="field-error">${this.orderFieldErrors.email}</small>` : nothing}
                    </label>
                    <label>
                      <span>${this.t.orderFormNotesLabel}</span>
                      <textarea rows="2" maxlength="1000" .value=${this.orderFormData.notes}
                        @input=${(e: Event) => this.updateOrderFormField('notes', (e.target as HTMLTextAreaElement).value)}
                        ?disabled=${this.orderFormSubmitting}></textarea>
                    </label>
                    ${this.orderFormError ? html`<div class="order-form-error">${this.orderFormError}</div>` : nothing}
                  </div>
                  <div class="cart-fog" ${ref(this.formFogRef)}></div>
                </div>
                <div class="cart-foot">
                  <div class="ttl"><span class="l">${this.t.orderTotalLabel}</span><span class="v">${totalStr}</span></div>
                  <button type="submit" class="cart-cta" ?disabled=${this.orderFormSubmitting}>
                    ${this.orderFormSubmitting ? '…' : this.t.orderFormSubmit} ${CHECK_ICON}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>`;
  }

  private openCartSheet(): void {
    if (this.order?.status !== 'draft' || !this.order.items.length) return;
    this.orderFormOpen = false; // always enter at step 1
    this.cartSheetOpen = true;
  }

  private closeCartSheet = (): void => {
    // Reset BOTH: `orderFormOpen` now means "sheet on step 2" and several chat-area
    // affordances are gated on `!orderFormOpen` (suggestions, input autofocus).
    // Leaving it true after a close-from-step-2 would wrongly keep them
    // suppressed while the sheet is gone.
    this.cartSheetOpen = false;
    this.orderFormOpen = false;
  };

  private backToCart = (): void => {
    this.orderFormOpen = false; // slide back to step 1; sheet stays open
  };

  private onCartBarKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.openCartSheet();
    }
  }

  private onSheetKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.closeCartSheet();
    }
  }

  // Scroll "fog": a soft gradient over the sticky footer while the region can
  // scroll further ("there's more below"); hidden once scrolled to the bottom.
  private updateFog(scroll?: HTMLElement | null, fog?: HTMLElement | null): void {
    if (!scroll || !fog) return;
    const remaining = scroll.scrollHeight - scroll.clientHeight - scroll.scrollTop;
    fog.classList.toggle('show', remaining > 6);
  }
  private onCartScroll(): void {
    this.updateFog(this.cartScrollRef.value, this.cartFogRef.value);
  }
  private onFormScroll(): void {
    this.updateFog(this.formScrollRef.value, this.formFogRef.value);
  }

  // --- Sheet drag (finger-tracking close) ---
  // The drag handlers live on the grip, which is interactive only while the sheet is open
  // (the closed sheet is `pointer-events:none`), so a drag always starts from the open
  // state: drag down to dismiss, release near the top snaps back open. Opening is a tap on
  // the collapsed bar. We only touch the sheet's transform once movement passes the tap
  // threshold, so a TAP stays a clean CSS class transition. The drag is hard-clamped to
  // [0, h] (no rubber-band). On release we set the final `.open` class IMPERATIVELY before
  // clearing the inline transform, so the sheet eases from the drag position to its resting
  // spot in one move — clearing first would snap to the not-yet-rendered class for a frame.
  private dragStartY: number | null = null;
  private dragPointerId: number | null = null;
  private dragStartOpen = false;
  private dragLastY = 0;
  private dragLastT = 0;
  private dragVelocity = 0; // px/ms, signed (down = +)
  private dragMoved = false;
  // offsetHeight is invariant during a drag (sheet is a fixed 90% height; translateY
  // doesn't change it), so cache it once at pointerdown instead of reading it — and
  // forcing a synchronous reflow after each transform write — on every pointermove.
  private dragSheetH = 1;

  private onSheetPointerDown(e: PointerEvent): void {
    if (this.order?.status !== 'draft' || !this.order.items.length) return;
    if (this.dragStartY !== null) return; // a drag is already in progress — ignore a 2nd finger
    this.dragPointerId = e.pointerId;
    this.dragStartY = e.clientY;
    this.dragStartOpen = this.cartSheetOpen;
    this.dragLastY = e.clientY;
    this.dragLastT = e.timeStamp;
    this.dragVelocity = 0;
    this.dragMoved = false;
    this.dragSheetH = this.sheetRef.value?.offsetHeight || 1;
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* capture unsupported (e.g. jsdom) — gesture still resolves on pointerup */
    }
  }

  private onSheetPointerMove(e: PointerEvent): void {
    if (this.dragStartY === null || e.pointerId !== this.dragPointerId) return;
    const dy = e.clientY - this.dragStartY;
    const dt = e.timeStamp - this.dragLastT;
    if (dt > 0) this.dragVelocity = (e.clientY - this.dragLastY) / dt;
    this.dragLastY = e.clientY;
    this.dragLastT = e.timeStamp;

    // Below the tap threshold: leave the sheet untouched so a tap stays a pure CSS
    // transition (no inline transform, no `dragging` class).
    if (!this.dragMoved && Math.abs(dy) <= SHEET_TAP_PX) return;
    e.preventDefault(); // it's a drag now — claim the vertical gesture

    const sheet = this.sheetRef.value;
    if (!sheet) return;
    if (!this.dragMoved) {
      this.dragMoved = true;
      sheet.classList.add('dragging'); // freeze the CSS transition for live tracking
    }
    const h = this.dragSheetH;
    const base = this.dragStartOpen ? 0 : h; // open starts at 0, closed fully down (h)
    const y = Math.max(0, Math.min(h, base + dy)); // hard clamp — no overshoot/bounce
    sheet.style.transform = `translateY(${y}px)`;
  }

  private onSheetPointerUp(e: PointerEvent): void {
    if (this.dragStartY === null || e.pointerId !== this.dragPointerId) return;
    const dy = e.clientY - this.dragStartY;
    const sheet = this.sheetRef.value;
    const h = this.dragSheetH;
    const moved = this.dragMoved;
    // A finger held still before lifting fires no pointermove, so dragVelocity would still
    // hold the last (possibly fast) sample — ignore it once the pointer has gone idle.
    const velocity = e.timeStamp - this.dragLastT > 100 ? 0 : this.dragVelocity;
    this.releaseDrag(e);

    if (!moved) {
      // Tap on the grip is a no-op — it's a drag handle, not a close button (a tap used to
      // collapse the whole sheet, dropping a half-filled order form). Explicit close lives
      // on the ✕, the scrim, Escape, and a deliberate downward drag.
      return;
    }

    const open = computeSheetSnap({
      startOpen: this.dragStartOpen,
      dyPx: dy,
      sheetHeightPx: h,
      velocityPxPerMs: velocity,
    }) === 'open';

    // Re-enable the transition + set the final class imperatively BEFORE clearing the
    // inline transform, so it eases from the drag position to the resting spot in one move.
    if (sheet) {
      sheet.classList.remove('dragging');
      sheet.classList.toggle('open', open);
      sheet.style.transform = '';
    }
    // Snap open: keep the sheet open WITHOUT resetting the step — a snap-back while on the
    // order form must not eject the user to step 1. Snap closed: dismiss the whole sheet.
    if (open) this.cartSheetOpen = true;
    else this.closeCartSheet();
  }

  private onSheetPointerCancel(e: PointerEvent): void {
    if (this.dragStartY === null || e.pointerId !== this.dragPointerId) return;
    this.releaseDrag(e);
    const sheet = this.sheetRef.value;
    if (sheet) {
      sheet.classList.remove('dragging');
      sheet.classList.toggle('open', this.cartSheetOpen); // revert to the current state
      sheet.style.transform = '';
    }
  }

  /** Clear drag state + release the pointer capture (shared by the up and cancel paths). */
  private releaseDrag(e: PointerEvent): void {
    this.dragStartY = null;
    this.dragMoved = false;
    this.dragPointerId = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* not captured / unsupported — implicit release on pointerup/cancel */
    }
  }

  /**
   * Default phone dial code for the forms = the BOT's CONFIGURED language
   * (pl→+48, de→+49…), taken from the bootstrap config — NOT the host page's
   * <html lang> or the browser. Falls back to the resolved UI locale, then +48.
   */
  private defaultDialCode(): string {
    const configLocale = (this.config?.locale ?? '').slice(0, 2).toLowerCase();
    return LOCALE_TO_CC[configLocale] ?? LOCALE_TO_CC[this.locale] ?? '+48';
  }

  /**
   * Seed the phone-country selectors from the configured language once bootstrap
   * resolves it — so the default dial code is correct before any form is opened
   * (and for non-PL configs). Only seeds selectors whose national field is still
   * empty (never clobbers a code the user already picked).
   */
  private seedDialCodeDefaults(): void {
    const cc = this.defaultDialCode();
    if (this.orderFormData.phone === '') this.orderPhoneCc = cc;
    if (this.escalationFormData.phone === '') this.escalationPhoneCc = cc;
    if (this.contactFormData.phone === '') this.contactPhoneCc = cc;
  }

  private openOrderForm(): void {
    if (!this.order || this.order.status !== 'draft' || this.order.items.length === 0) return;
    // Prefill from the server draft (bot may have collected some data over chat) ONLY on a
    // fresh form — once the user has edited anything, re-entering (step 2 → back → CTA) must
    // NOT clobber their input. Split the phone into dial code (selector) + grouped national.
    if (!this.orderFormDirty) {
      const { cc, national } = splitPhone(this.order.customer.phone ?? '', this.defaultDialCode());
      this.orderPhoneCc = cc;
      this.orderFormData = {
        name: this.order.customer.name ?? '',
        phone: national,
        email: this.order.customer.email ?? '',
        notes: '',
      };
      this.orderFieldErrors = {};
    }
    this.orderFormError = null;
    this.cartSheetOpen = true; // ensure the overlay is visible
    this.orderFormOpen = true; // step 2
  }

  private updateOrderFormField(field: 'name' | 'phone' | 'email' | 'notes', value: string): void {
    this.orderFormData = { ...this.orderFormData, [field]: value };
    this.orderFormDirty = true;
    if (field !== 'notes') this.clearFieldError(field); // clear the inline error as the user edits
  }

  /** Drop one field's inline error (immutably, so Lit re-renders). */
  private clearFieldError(field: 'name' | 'phone' | 'email'): void {
    if (this.orderFieldErrors[field] === undefined) return;
    const next = { ...this.orderFieldErrors };
    delete next[field];
    this.orderFieldErrors = next;
  }

  // --- Phone fields (order + escalation): dial-code selector + grouped national input ---
  /** National-digit cap for the selected dial code = that country's max length (PHONE_LEN);
   *  unlisted code falls back to the E.164 ceiling (15) minus the country-code digits. */
  private nationalMaxDigits(cc: string): number {
    const win = PHONE_LEN[cc];
    return win ? win[1] : 15 - cc.replace(/\D/g, '').length;
  }
  /** Compose the submitted/validated value "<cc> <national>"; an empty national yields ""
   *  (so an optional / not-yet-filled phone reads as "no number", not a bare dial code). */
  private composePhone(cc: string, national: string): string {
    const n = national.trim();
    if (n === '') return '';
    return cc === OTHER_CC ? n : `${cc} ${n}`; // "Other": the user typed the full "+cc national"
  }

  /** Format the national input for the selected dial code: free-form for "Other", otherwise
   *  grouped national capped to the country's max length. */
  private formatPhoneField(cc: string, value: string): string {
    return cc === OTHER_CC ? formatIntlInput(value) : groupNational(value, this.nationalMaxDigits(cc));
  }

  /** On a dial-code change, convert the field between grouped-national and the free-form
   *  "Other" full-number mode so the value stays consistent. */
  private reframePhone(prev: string, next: string, value: string): string {
    const v = value.trim();
    if (next === OTHER_CC && prev !== OTHER_CC) return v === '' ? '' : `${prev} ${v}`; // → "+cc national"
    if (prev === OTHER_CC && next !== OTHER_CC) return ''; // leaving free-form → clean slate
    // country → country: re-group AND re-cap do maks. długości NOWEGO kraju
    // (inaczej cyfry z dłuższego planu zostawały ponad limit nowego kraju).
    return this.formatPhoneField(next, v);
  }

  private onPhoneCcChange(e: Event): void {
    const prev = this.orderPhoneCc;
    this.orderPhoneCc = (e.target as HTMLSelectElement).value;
    this.orderFormData = {
      ...this.orderFormData,
      phone: this.reframePhone(prev, this.orderPhoneCc, this.orderFormData.phone),
    };
    this.orderFormDirty = true;
    this.clearFieldError('phone');
  }
  private onPhoneNationalInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    const formatted = this.formatPhoneField(this.orderPhoneCc, input.value);
    if (input.value !== formatted) input.value = formatted; // strip letters / regroup live
    this.updateOrderFormField('phone', formatted);
  }
  private orderPhoneValue(): string {
    return this.composePhone(this.orderPhoneCc, this.orderFormData.phone);
  }

  private onEscalationCcChange(e: Event): void {
    const prev = this.escalationPhoneCc;
    this.escalationPhoneCc = (e.target as HTMLSelectElement).value;
    this.escalationFormData = {
      ...this.escalationFormData,
      phone: this.reframePhone(prev, this.escalationPhoneCc, this.escalationFormData.phone),
    };
  }
  private onEscalationNationalInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    const formatted = this.formatPhoneField(this.escalationPhoneCc, input.value);
    if (input.value !== formatted) input.value = formatted;
    this.updateEscalationField('phone', formatted);
  }
  private escalationPhoneValue(): string {
    return this.composePhone(this.escalationPhoneCc, this.escalationFormData.phone);
  }

  private async submitOrderForm(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (!this.client || !this.conversationId || this.orderFormSubmitting) return;

    const { name, email, notes } = this.orderFormData;
    const phone = this.orderPhoneValue(); // dial code + national
    // Per-field client validation → inline errors under each field (mirror the server
    // to avoid a 422 round-trip; the server re-checks everything as the real gate).
    const fieldErrors: { name?: string; phone?: string; email?: string } = {};
    if (name.trim().length < 2) fieldErrors.name = this.t.orderFormErrorName;
    if (!isValidPhone(phone)) fieldErrors.phone = this.t.orderFormErrorPhone;
    if (!isValidEmail(email)) fieldErrors.email = this.t.orderFormErrorEmail;
    if (Object.keys(fieldErrors).length > 0) {
      this.orderFieldErrors = fieldErrors;
      this.orderFormError = null;
      return;
    }

    this.orderFormSubmitting = true;
    this.orderFieldErrors = {};
    this.orderFormError = null;

    try {
      const result = await this.client.submitOrderForm(this.conversationId, {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        notes: notes.trim() || undefined,
      });

      this.order = result.order;
      this.orderFormOpen = false;
      this.cartSheetOpen = false;
      this.orderFormDirty = false; // submitted → a fresh draft should prefill again
      // Append the server's confirmation as a thread entry (same string the bot
      // submit returns: "order placed" + a markdown summary that renders as bullets).
      this.messages = [
        ...this.messages,
        {
          id: `local-${Date.now()}`,
          role: 'assistant',
          content: result.confirmation,
        },
      ];
    } catch (err) {
      if (err instanceof WidgetApiException) {
        // Map field codes to inline errors (HTML-tamper backstop); anything else
        // (empty_cart / already_submitted / network) → the general banner.
        const serverFieldErrors: { name?: string; phone?: string; email?: string } = {};
        let general: string | null = null;
        for (const er of err.errors) {
          if (er.code === 'invalid_name') serverFieldErrors.name = this.t.orderFormErrorName;
          else if (er.code === 'invalid_phone') serverFieldErrors.phone = this.t.orderFormErrorPhone;
          else if (er.code === 'invalid_email') serverFieldErrors.email = this.t.orderFormErrorEmail;
          else general = er.message ?? this.t.genericError;
        }
        this.orderFieldErrors = serverFieldErrors;
        this.orderFormError = general;
      } else {
        this.orderFormError = this.t.genericError;
      }
    } finally {
      this.orderFormSubmitting = false;
    }
  }

  /**
   * Zmiana znacznika requested_at przy aktywnym sygnale = bot poprosił PONOWNIE →
   * formularz przenosi się pod najnowszą odpowiedź (przeniesienie = usunięcie
   * starego i pokazanie nowego; formularz to jeden byt zakotwiczony w liście).
   * Wspólny predykat obu bliźniaczych formularzy (kontakt/eskalacja) — muszą
   * zachowywać się identycznie.
   */
  private isRenewedRequest(active: boolean, next: string | null, prev: string | null): boolean {
    return active && next !== null && prev !== null && next !== prev;
  }

  /**
   * Reflect the escalation payload signal. undefined = key absent (leave state);
   * null = no open escalation (clear); object = bot escalated → show/keep the form
   * (or its submitted state, which survives reload via contact_submitted).
   */
  private applyEscalationSignal(signal: EscalationSignal | null | undefined): void {
    if (signal === undefined) return;
    const active = signal?.active ?? false;
    const wasActive = this.escalationActive;
    const requestedAt = signal?.requested_at ?? null;
    const renewed = this.isRenewedRequest(active, requestedAt, this.escalationRequestedAt);
    this.escalationActive = active;
    this.escalationSubmitted = signal?.contact_submitted ?? false;
    this.escalationRequestedAt = requestedAt;
    // Seed the dial code from the bot's locale country when the form first appears — don't
    // clobber a code the user already picked.
    if (this.escalationActive && (!wasActive || renewed)) {
      // Ta sama kotwica co przy kontakcie — bliźniacze formularze muszą zachowywać
      // się tak samo, inaczej jeden przewija się z rozmową, a drugi wisi na dnie.
      this.escalationAnchorId = this.anchorForRequest(requestedAt);
      // Seed TYLKO przy pierwszym pojawieniu się — renewed nie może nadpisać
      // kierunkowego wybranego przez klienta, zanim wpisał numer.
      if (!wasActive && this.escalationFormData.phone === '') {
        this.escalationPhoneCc = this.defaultDialCode();
      }
    }
  }

  private updateEscalationField(field: 'name' | 'email' | 'phone' | 'message', value: string): void {
    this.escalationFormData = { ...this.escalationFormData, [field]: value };
  }

  /**
   * Reflect the contact-request payload signal (np. leady). undefined = key absent
   * (leave state); null = no request (hide); object = bot needs contact → show form.
   */
  private applyContactSignal(signal: ContactSignal | null | undefined): void {
    if (signal === undefined) return;
    const active = signal?.active ?? false;
    const purpose = signal?.purpose ?? 'lead';

    const wasActive = this.contactActive;
    const requestedAt = signal?.requested_at ?? null;
    const renewed = this.isRenewedRequest(active, requestedAt, this.contactRequestedAt);
    this.contactActive = active;
    this.contactPurpose = purpose;
    this.contactMissing = signal?.missing ?? [];
    this.contactRequestedAt = requestedAt;
    if (this.contactActive && (!wasActive || renewed)) {
      // Świeża prośba → przypnij formularz do wiadomości, w której bot o kontakt
      // poprosił (wg requested_at; po reloadzie = pierwotne miejsce). Kolejne
      // wiadomości wejdą pod nim.
      this.contactAnchorId = this.anchorForRequest(requestedAt);
      // Prefill z serwera (tryb edycji / dane częściowe) — wypełniamy TYLKO
      // puste pola, nigdy nie nadpisujemy tego, co klient już wpisał.
      const prefill = signal?.prefill;
      if (prefill) {
        const next = { ...this.contactFormData };
        if (next.name === '' && prefill.name) next.name = prefill.name;
        if (next.email === '' && prefill.email) next.email = prefill.email;
        if (next.phone === '' && prefill.phone) {
          const { cc, national } = splitPhone(prefill.phone, this.defaultDialCode());
          this.contactPhoneCc = cc;
          next.phone = national;
        }
        this.contactFormData = next;
      }
      // A fresh request (re-activation) clears the one-shot submitted flag so the
      // form can reappear for a second lead / a new conversation — without this the
      // form stays hidden for the widget's lifetime after the first submit.
      this.contactSubmitted = false;
      // Seed TYLKO przy pierwszym pojawieniu się — jak wyżej w eskalacji.
      if (!wasActive && this.contactFormData.phone === '') {
        this.contactPhoneCc = this.defaultDialCode();
      }
    }
  }

  private updateContactField(field: 'name' | 'email' | 'phone', value: string): void {
    this.contactFormData = { ...this.contactFormData, [field]: value };
  }

  private readonly onContactCcChange = (e: Event): void => {
    const prev = this.contactPhoneCc;
    this.contactPhoneCc = (e.target as HTMLSelectElement).value;
    this.contactFormData = {
      ...this.contactFormData,
      phone: this.reframePhone(prev, this.contactPhoneCc, this.contactFormData.phone),
    };
  };

  private readonly onContactNationalInput = (e: Event): void => {
    const input = e.target as HTMLInputElement;
    this.contactFormData = { ...this.contactFormData, phone: this.formatPhoneField(this.contactPhoneCc, input.value) };
  };

  private contactPhoneValue(): string {
    return this.composePhone(this.contactPhoneCc, this.contactFormData.phone);
  }

  /** Whether the contact form must collect this field (empty missing = collect all). */
  private contactNeeds(field: string): boolean {
    return this.contactMissing.length === 0 || this.contactMissing.includes(field);
  }

  private async submitContact(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (!this.client || !this.conversationId || this.contactFormSubmitting) return;

    const { name, email } = this.contactFormData;
    const phone = this.contactPhoneValue();
    // Require only the fields the server still needs (the rendered inputs). A single
    // x-pii action may need just one field; a returning visitor only the missing one.
    if (
      (this.contactNeeds('name') && name.trim() === '') ||
      (this.contactNeeds('email') && email.trim() === '') ||
      (this.contactNeeds('phone') && phone.trim() === '')
    ) {
      this.contactFormError = this.t.escalationErrorRequired;
      return;
    }
    if (this.contactNeeds('phone') && !isValidPhone(phone)) {
      this.contactFormError = this.t.escalationErrorPhone;
      return;
    }

    this.contactFormSubmitting = true;
    this.contactFormError = null;
    try {
      const { contact } = await this.client.submitContact(this.conversationId, {
        name,
        email,
        phone,
        purpose: this.contactPurpose,
      });
      this.contactSubmitted = true;
      this.applyContactSignal(contact ?? { active: false, purpose: this.contactPurpose, missing: [] });
      this.messages = [
        ...this.messages,
        { id: `local-${Date.now()}`, role: 'assistant', content: this.t.contactSubmittedConfirmation },
      ];
    } catch (err) {
      const code = err instanceof WidgetApiException ? err.errors[0]?.code : undefined;
      this.contactFormError = code === 'invalid_phone' ? this.t.escalationErrorPhone : this.t.genericError;
    } finally {
      this.contactFormSubmitting = false;
    }
  }

  private renderContactForm() {
    if (!this.contactActive || this.contactSubmitted) return nothing;
    return html`
      <div class="order-form esc-form" role="dialog" aria-label=${this.t.contactFormTitle}>
        <div class="order-form-header">
          <div class="esc-icon">${ICON_CHAT}</div>
          <h3>${this.t.contactFormTitle}</h3>
          <p>${this.t.contactFormSubtitle}</p>
        </div>
        <form @submit=${(e: SubmitEvent) => this.submitContact(e)}>
          ${this.contactNeeds('name')
            ? html`<label>
            <span>${this.t.escalationNameLabel}</span>
            <input type="text" required maxlength="200" .value=${this.contactFormData.name}
              @input=${(e: Event) => this.updateContactField('name', (e.target as HTMLInputElement).value)}
              ?disabled=${this.contactFormSubmitting} />
          </label>`
            : nothing}
          ${this.contactNeeds('email')
            ? html`<label>
            <span>${this.t.escalationEmailLabel}</span>
            <input type="email" required maxlength="255" .value=${this.contactFormData.email}
              @input=${(e: Event) => this.updateContactField('email', (e.target as HTMLInputElement).value)}
              ?disabled=${this.contactFormSubmitting} />
          </label>`
            : nothing}
          ${this.contactNeeds('phone')
            ? html`<label>
            <span>${this.t.escalationPhoneLabel}</span>
            ${this.renderPhoneRow({
              cc: this.contactPhoneCc,
              national: this.contactFormData.phone,
              label: this.t.escalationPhoneLabel,
              required: true,
              valid: this.contactFormData.phone.trim() !== '' && isValidPhone(this.contactPhoneValue()),
              disabled: this.contactFormSubmitting,
              onCc: this.onContactCcChange,
              onNational: this.onContactNationalInput,
            })}
          </label>`
            : nothing}
          ${this.contactFormError ? html`<div class="order-form-error">${this.contactFormError}</div>` : nothing}
          <div class="order-form-actions">
            <button type="submit" class="submit" ?disabled=${this.contactFormSubmitting}>
              ${this.t.contactSubmitButton}
            </button>
          </div>
        </form>
      </div>
    `;
  }

  private async submitEscalation(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    if (!this.client || !this.conversationId || this.escalationFormSubmitting) return;

    const { name, email, message } = this.escalationFormData;
    const phone = this.escalationPhoneValue(); // dial code + national ("" when blank)
    if (!name.trim() || !email.trim() || !message.trim() || !isValidEmail(email)) {
      this.escalationFormError = this.t.escalationErrorRequired;
      return;
    }
    // Phone optional, but if given must validate (mirror of the server).
    if (phone !== '' && !isValidPhone(phone)) {
      this.escalationFormError = this.t.escalationErrorPhone;
      return;
    }

    this.escalationFormSubmitting = true;
    this.escalationFormError = null;

    try {
      await this.client.submitEscalation(this.conversationId, {
        name: name.trim(),
        email: email.trim(),
        phone: phone || undefined,
        message: message.trim(),
      });
      this.escalationSubmitted = true;
      this.messages = [
        ...this.messages,
        { id: `local-${Date.now()}`, role: 'assistant', content: this.t.escalationSubmittedConfirmation },
      ];
    } catch (err) {
      if (err instanceof WidgetApiException) {
        const code = err.errors[0]?.code;
        this.escalationFormError = code === 'invalid_phone'
          ? this.t.escalationErrorPhone
          : (err.errors[0]?.message ?? this.t.genericError);
      } else {
        this.escalationFormError = this.t.genericError;
      }
    } finally {
      this.escalationFormSubmitting = false;
    }
  }

  private renderEscalationForm() {
    if (!this.escalationActive || this.escalationSubmitted) return nothing;
    return html`
      <div class="order-form esc-form" role="dialog" aria-label=${this.t.escalationFormTitle}>
        <div class="order-form-header">
          <div class="esc-icon">${ICON_CHAT}</div>
          <h3>${this.t.escalationFormTitle}</h3>
          <p>${this.t.escalationFormSubtitle}</p>
        </div>
        <form @submit=${(e: SubmitEvent) => this.submitEscalation(e)}>
          <label>
            <span>${this.t.escalationNameLabel}</span>
            <input
              type="text"
              required
              maxlength="200"
              .value=${this.escalationFormData.name}
              @input=${(e: Event) => this.updateEscalationField('name', (e.target as HTMLInputElement).value)}
              ?disabled=${this.escalationFormSubmitting}
            />
          </label>
          <label>
            <span>${this.t.escalationEmailLabel}</span>
            <input
              type="email"
              required
              maxlength="255"
              .value=${this.escalationFormData.email}
              @input=${(e: Event) => this.updateEscalationField('email', (e.target as HTMLInputElement).value)}
              ?disabled=${this.escalationFormSubmitting}
            />
          </label>
          <label>
            <span>${this.t.escalationPhoneLabel} <small>${this.t.escalationPhoneOptional}</small></span>
            ${this.renderPhoneRow({
              cc: this.escalationPhoneCc,
              national: this.escalationFormData.phone,
              label: this.t.escalationPhoneLabel,
              required: false,
              valid: this.escalationFormData.phone.trim() !== '' && isValidPhone(this.escalationPhoneValue()),
              disabled: this.escalationFormSubmitting,
              onCc: this.onEscalationCcChange,
              onNational: this.onEscalationNationalInput,
            })}
          </label>
          <label>
            <span>${this.t.escalationMessageLabel}</span>
            <textarea
              rows="2"
              maxlength="2000"
              .value=${this.escalationFormData.message}
              @input=${(e: Event) => this.updateEscalationField('message', (e.target as HTMLTextAreaElement).value)}
              ?disabled=${this.escalationFormSubmitting}
            ></textarea>
          </label>
          ${this.escalationFormError
            ? html`<div class="order-form-error">${this.escalationFormError}</div>`
            : nothing}
          <div class="order-form-actions">
            <button type="submit" class="submit" ?disabled=${this.escalationFormSubmitting}>
              ${this.escalationFormSubmitting ? '...' : this.t.escalationSubmit}
            </button>
          </div>
        </form>
      </div>
    `;
  }

  /**
   * Renderuje treść asystenta z lekkim markdown: **pogrubienie** → <strong>,
   * znaki nowej linii → <br>, oraz listy „- "/„* " → prawdziwe punkty <ul>/<li>
   * (model często formatuje podsumowania jako listę markdown — bez tego widać było
   * surowy „-"). Parytet z Message::renderedContent() w panelu/sandbox. Bez
   * unsafeHTML — budujemy Lit template parts, więc treść jest auto-escaped (XSS-safe).
   */
  private renderMessageContent(content: string) {
    const lines = content.split('\n');
    const parts: unknown[] = [];
    let bullets: string[] = [];
    let needBreak = false; // emit a <br> before the next inline (text) line
    const flushBullets = () => {
      if (bullets.length === 0) return;
      const items = bullets;
      bullets = [];
      parts.push(
        html`<ul class="msg-list">
          ${items.map((it) => html`<li>${this.renderInlineMarkup(it)}</li>`)}
        </ul>`,
      );
      needBreak = false; // the block list already separates following content
    };
    for (const line of lines) {
      const bullet = line.match(/^[ \t]*[-*][ \t]+(.+)$/); // leading "- "/"* " only (em-dash mid-line stays text)
      if (bullet) {
        bullets.push(bullet[1]);
        continue;
      }
      if (line.trim() === '' && bullets.length > 0) continue; // keep a list cohesive across a blank line
      flushBullets();
      parts.push(html`${needBreak ? html`<br />` : nothing}${this.renderInlineMarkup(line)}`);
      needBreak = true;
    }
    flushBullets();
    return parts;
  }

  private renderInlineMarkup(text: string) {
    const parts: unknown[] = [];
    const regex = /\*\*([^*]+?)\*\*/g;
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > last) parts.push(...this.renderLinks(text.slice(last, match.index)));
      parts.push(html`<strong>${match[1]}</strong>`);
      last = match.index + match[0].length;
    }
    if (last < text.length) parts.push(...this.renderLinks(text.slice(last)));
    return parts;
  }

  /**
   * Turn http(s):// URLs in a plain-text run into clickable <a> parts (bare
   * domains are intentionally NOT linked — see format-links.ts). Built
   * as Lit template parts (not unsafeHTML), so the link label + surrounding text
   * stay auto-escaped — only the href/structure is ours.
   */
  private renderLinks(text: string) {
    return linkifyTokens(text).map((tok) =>
      typeof tok === 'string'
        ? tok
        : html`<a
            href=${tok.href}
            rel="noopener noreferrer nofollow"
            class="msg-link"
            >${tok.url}</a
          >`,
    );
  }

  /**
   * "X minut temu" under a final bot bubble. Rendered only when the message has a
   * server timestamp — so the local greeting and the streaming placeholder (no
   * created_at until `done`) get none, and it never sits under product cards.
   * Re-renders every 60s via timeTicker; the exact time is in the title tooltip.
   * Clock skew is clamped to 0 so a slightly-ahead server never shows "in 2 min".
   */
  private renderMsgTime(m: ChatMessage) {
    if (!m.created_at) return nothing;
    const ts = Date.parse(m.created_at);
    if (Number.isNaN(ts)) return nothing;
    const delta = Math.max(0, (Date.now() - ts) / 1000);
    return html`<div class="msg-time" title=${new Date(ts).toLocaleString()}>${formatRelativeTime(this.locale, delta, this.t.timeAgoNow)}</div>`;
  }

  /**
   * Renders a message's products in the owner-chosen layout: compact rows, or rich
   * cards (a single card, or a horizontal carousel when there are several). Only the
   * data we actually have is shown — no rating / old price / availability / tags.
   */
  private renderProductGroup(products: ProductCard[]) {
    if (this.productDisplayMode === 'compact') {
      return html`
        <div class="product-group">
          ${repeat(products, (p) => p.product_id, (p) => this.renderProductCompact(p))}
        </div>
      `;
    }

    if (products.length <= 1) {
      return html`
        <div class="product-group">
          ${products.map((p) => this.renderProductCardRich(p))}
        </div>
      `;
    }

    const key = products.map((p) => p.product_id).join(',');
    return html`
      <div class="product-group pcarousel">
        <div class="embla" ${emblaCarousel(key)}>
          <div class="embla__container">
            ${repeat(
              products,
              (p) => p.product_id,
              (p) => html`<div class="embla__slide">${this.renderProductCardRich(p)}</div>`,
            )}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Re-bootstrap tylko po świeży external_cart — najwyżej RAZ na życie widgetu
   * (restore historii z wieloma pending opami nie może zasypać serwera). Błąd
   * sieci = zostaje stary config (caller pokaże chip błędu).
   */
  private refreshExternalCartConfig(): Promise<void> {
    if (!this.client) {
      return Promise.resolve();
    }
    this.externalCartRefresh ??= this.client
      .bootstrap()
      .then((fresh) => {
        if (this.config) {
          this.config = { ...this.config, external_cart: fresh.external_cart };
        }
      })
      .catch(() => undefined);
    return this.externalCartRefresh;
  }

  /** external_cart mode active: toggle+plan+endpoint confirmed server-side in bootstrap. */
  private externalCartEnabled(): boolean {
    return this.config?.external_cart?.enabled === true && !!this.config.external_cart.endpoint_url;
  }

  // Require a product_id so a card with an empty id never shows a dead "add" button
  // (parity with the Blade card; the POST would otherwise 422 on a required id).
  // External mode: a variantless product needs its shop SKU; variant products
  // resolve the SKU through the selector (variant_sku).
  private canAddToCart(p: ProductCard): boolean {
    if (this.externalCartEnabled()) {
      return this.hasVariants(p) ? true : !!p.sku;
    }
    // Internal orders: produkt bez ceny karty (= żadna fasetka nie jest wyceniona;
    // cena ukryta — spec null-price-path) nie jest zamawialny przez czat; serwer
    // odrzuca, więc nie renderujemy martwego przycisku. External cart: bez zmian.
    return this.config?.orders_enabled === true && p.product_id !== '' && visibleCents(p.price_cents) !== null;
  }

  private hasVariants(p: ProductCard): boolean {
    return (p.variants?.length ?? 0) > 0;
  }

  /** Axis keys (stable first-seen order) + their distinct values across a product's variants. */
  private variantAxes(p: ProductCard): Array<{ key: string; values: string[] }> {
    const cached = variantAxesCache.get(p);
    if (cached) return cached;
    const order: string[] = [];
    const byKey: Record<string, string[]> = {};
    for (const v of p.variants ?? []) {
      for (const [k, val] of Object.entries(v.axes)) {
        if (!(k in byKey)) {
          byKey[k] = [];
          order.push(k);
        }
        if (!byKey[k].includes(val)) byKey[k].push(val);
      }
    }
    const result = order.map((key) => ({ key, values: byKey[key] }));
    variantAxesCache.set(p, result);
    return result;
  }

  /** The variant matching ALL currently-selected axes — null until a full combination is picked. */
  private selectedVariant(p: ProductCard): ProductVariant | null {
    const axes = this.variantAxes(p);
    const sel = this.variantSelections[p.product_id] ?? {};
    if (axes.some((a) => !sel[a.key])) return null;
    return (p.variants ?? []).find((v) => axes.every((a) => v.axes[a.key] === sel[a.key])) ?? null;
  }

  /**
   * Is an axis value offered at all — i.e. does SOME available variant carry it?
   * Evaluated INDEPENDENTLY of the other axes' current picks (only a value with no
   * available variant anywhere is disabled). Cross-axis incompatibility is resolved
   * on select (prune) + by the Add button staying disabled until selectedVariant
   * resolves — so a non-rectangular matrix can never dead-end the user on one axis.
   */
  private axisValueAvailable(p: ProductCard, axisKey: string, value: string): boolean {
    return (p.variants ?? []).some((v) => v.available && v.axes[axisKey] === value);
  }

  private selectVariantAxis(p: ProductCard, axisKey: string, value: string): void {
    const next: Record<string, string> = { ...(this.variantSelections[p.product_id] ?? {}), [axisKey]: value };
    // Drop any OTHER axis pick that no longer co-exists with the new one in an
    // available variant, so the user never sits on an impossible combination.
    for (const k of Object.keys(next)) {
      if (k === axisKey) continue;
      const stillOk = (p.variants ?? []).some(
        (v) => v.available && v.axes[axisKey] === value && v.axes[k] === next[k],
      );
      if (!stillOk) delete next[k];
    }
    this.variantSelections = { ...this.variantSelections, [p.product_id]: next };
  }

  /** Variant-aware unit price (selected variant → its price, falling back to the product's). */
  private cardPriceCents(p: ProductCard, v: ProductVariant | null): number | null {
    if (v) return visibleCents(v.price_cents ?? p.price_cents);
    return visibleCents(p.price_cents);
  }

  private formatCents(cents: number | null, currency: string): string | null {
    return cents !== null ? `${(cents / 100).toFixed(2)} ${currency}` : null;
  }

  /** Total item count in the draft cart (sum of line quantities). */
  private cartItemCount(): number {
    return (this.order?.items ?? []).reduce((sum, i) => sum + i.qty, 0);
  }

  /** Add allowed once a no-variant product, OR a fully-selected available variant, is ready. */
  private canAddCard(p: ProductCard, v: ProductVariant | null): boolean {
    if (!this.canAddToCart(p)) return false;
    if (!this.hasVariants(p)) return true;
    if (v === null || !v.available) return false;
    // Wariantowy price_cents jest już EFEKTYWNY (serwer: wariant ?? baza) —
    // null/0 = cena ukryta; przycisk zostaje disabled zamiast martwego requestu.
    return this.externalCartEnabled() || visibleCents(v.price_cents) !== null;
  }

  /** Axis selector — chips (buttons) or a compact dropdown, per owner setting. */
  private renderVariantSelector(p: ProductCard) {
    return this.variantSelectorStyle === 'dropdown'
      ? this.renderVariantDropdowns(p)
      : this.renderVariantChips(p);
  }

  /** Chips: every option visible (default). */
  private renderVariantChips(p: ProductCard) {
    const sel = this.variantSelections[p.product_id] ?? {};
    return html`
      <div class="pvariants">
        ${this.variantAxes(p).map(
          (axis) => html`
            <div class="pvaxis">
              <span class="pvaxis-label">${this.t.variantAxisLabels[axis.key] ?? axis.key}</span>
              <div class="pvaxis-opts">
                ${axis.values.map(
                  (val) => html`<button
                    class="pvchip ${sel[axis.key] === val ? 'sel' : ''}"
                    type="button"
                    ?disabled=${!this.axisValueAvailable(p, axis.key, val)}
                    @click=${() => this.selectVariantAxis(p, axis.key, val)}
                  >${val}</button>`,
                )}
              </div>
            </div>
          `,
        )}
      </div>
    `;
  }

  /** Compact dropdowns: one inline (label + select) row per axis. */
  private renderVariantDropdowns(p: ProductCard) {
    const sel = this.variantSelections[p.product_id] ?? {};
    return html`
      <div class="pvariants pvariants-dd">
        ${this.variantAxes(p).map(
          (axis) => html`
            <label class="pvaxis-row">
              <span class="pvaxis-label">${this.t.variantAxisLabels[axis.key] ?? axis.key}</span>
              <select
                class="pvselect"
                @change=${(e: Event) => this.selectVariantAxis(p, axis.key, (e.target as HTMLSelectElement).value)}
              >
                <option value="" ?selected=${!sel[axis.key]} disabled>${this.t.variantPrompt}</option>
                ${axis.values.map(
                  (val) => html`<option
                    value=${val}
                    ?selected=${sel[axis.key] === val}
                    ?disabled=${!this.axisValueAvailable(p, axis.key, val)}
                  >${val}</option>`,
                )}
              </select>
            </label>
          `,
        )}
      </div>
    `;
  }

  /** Rich vertical card (card mode). */
  private renderProductCardRich(p: ProductCard) {
    const hasVariants = this.hasVariants(p);
    const v = hasVariants ? this.selectedVariant(p) : null;
    const price = this.formatCents(this.cardPriceCents(p, v), p.currency);
    const showAdd = this.canAddToCart(p); // button present in principle…
    const canAdd = this.canAddCard(p, v); // …enabled once a valid variant (if any) is chosen
    return html`
      <div class="pcard">
        <div class="ph">
          ${p.image_url
            ? html`<img src=${safeUrl(p.image_url)} alt=${p.name} loading="lazy" draggable="false">`
            : html`<span class="ph-ph">${ICON_IMG}</span>`}
        </div>
        <div class="pb">
          ${/* Nazwa = link do produktu (ta sama karta — klient zostaje w sklepie). */ ''}
          <div class="pn">${p.url
            ? html`<a class="pn-link" href=${safeUrl(p.url)}>${p.name}</a>`
            : p.name}</div>
          ${p.short_description ? html`<div class="pd">${p.short_description}</div>` : nothing}
          ${hasVariants ? this.renderVariantSelector(p) : nothing}
          ${price
            ? html`<div class="pprice">${price}</div>`
            : nothing}
          ${p.url || showAdd
            ? html`<div class="pacts">
                ${p.url
                  ? html`<a class="pact details" href=${safeUrl(p.url)}>${this.t.productDetails}</a>`
                  : nothing}
                ${showAdd
                  ? html`<button
                      class="pact add"
                      type="button"
                      ?disabled=${this.addingProductId === p.product_id || !canAdd}
                      title=${hasVariants && !canAdd ? (v ? this.t.checkPrice : this.t.variantPrompt) : nothing}
                      @click=${() => this.addToCart(p)}
                    >${this.t.addToCartShort}</button>`
                  : nothing}
              </div>`
            : nothing}
        </div>
      </div>
    `;
  }

  /** Compact row (compact mode). */
  private renderProductCompact(p: ProductCard) {
    const hasVariants = this.hasVariants(p);
    const v = hasVariants ? this.selectedVariant(p) : null;
    const price = this.formatCents(this.cardPriceCents(p, v), p.currency);
    const showAdd = this.canAddToCart(p);
    const canAdd = this.canAddCard(p, v);
    return html`
      <div class="ptile">
        <div class="pt-img">
          ${p.image_url
            ? html`<img src=${safeUrl(p.image_url)} alt=${p.name} loading="lazy" draggable="false">`
            : html`<span class="pt-ph">${ICON_IMG}</span>`}
        </div>
        <div class="pt-b">
          <div class="pt-n">${p.url
            ? html`<a class="pn-link" href=${safeUrl(p.url)}>${p.name}</a>`
            : p.name}</div>
          ${p.short_description ? html`<div class="pt-desc">${p.short_description}</div>` : nothing}
          ${hasVariants ? this.renderVariantSelector(p) : nothing}
          <div class="pt-row">
            ${price
              ? html`<span class="pt-p">${price}</span>`
              : nothing}
            ${p.url || showAdd
              ? html`<div class="pt-iacts">
                  ${p.url
                    ? html`<a class="pt-ico" href=${safeUrl(p.url)} title=${this.t.productDetails} aria-label=${this.t.productDetails}>${ICON_EYE}</a>`
                    : nothing}
                  ${showAdd
                    ? html`<button
                        class="pt-ico cart"
                        type="button"
                        ?disabled=${this.addingProductId === p.product_id || !canAdd}
                        @click=${() => this.addToCart(p)}
                        title=${hasVariants && !canAdd ? (v ? this.t.checkPrice : this.t.variantPrompt) : this.t.addToCart}
                        aria-label=${this.t.addToCart}
                      >${ICON_CART}</button>`
                    : nothing}
                </div>`
              : nothing}
          </div>
        </div>
      </div>
    `;
  }


  /**
   * One-click "Dodaj do koszyka" from a product card. Adds the product server-side
   * (no LLM turn) and reflects the result in place: the cart bar (order) + a "Dodano X"
   * breadcrumb line in the transcript (a user-role message the bot also sees next turn).
   */
  private async addToCart(p: ProductCard): Promise<void> {
    if (!this.client || !this.conversationId || this.addingProductId !== null) {
      return;
    }
    // Variant products: never POST without a fully-selected, available variant
    // (the button is disabled until then; this is belt-and-suspenders).
    const variant = this.hasVariants(p) ? this.selectedVariant(p) : null;
    if (this.hasVariants(p) && (variant === null || !variant.available)) {
      return;
    }
    // External mode: the SHOP executes the add (same-origin fetch, visitor's
    // session) — never the builtin /cart route (R.10: builtin XOR external).
    if (this.externalCartEnabled()) {
      await this.addToShopCart(p, variant);
      return;
    }
    this.addingProductId = p.product_id;
    try {
      const res = await this.client.addToCart(this.conversationId, p.product_id, variant?.variant_sku);
      this.order = res.order;
      // Keep the product's card visible after adding (re-clicking just adds another —
      // standard "add to cart"). Removing it made the product the customer just added
      // vanish from the chat, which read as a bug.
      this.messages = [
        ...this.messages,
        { id: res.message.id, role: 'user', content: res.message.content },
      ];
      this.nextScroll = 'bottom';
      await this.updateComplete;
      this.scrollToBottom();
    } catch (e) {
      // Surface the failure (e.g. the variant just went out of stock → 422) in the
      // error banner instead of a dead click with no feedback. Clears on next send.
      this.lastError = this.formatError(e);
    } finally {
      this.addingProductId = null;
    }
  }

  private formatError(err: unknown): string {
    if (err instanceof WidgetApiException) {
      return err.errors[0]?.message ?? `${this.t.errorStatusPrefix} (${err.status})`;
    }
    return this.t.genericError;
  }

  /**
   * Card click in external mode: same-origin fetch to the shop endpoint (the
   * visitor's own session), then report the outcome so the transcript gets the
   * server's templated breadcrumb (appended in place from the response).
   */
  private async addToShopCart(p: ProductCard, variant: ProductVariant | null): Promise<void> {
    const endpoint = this.config?.external_cart?.endpoint_url ?? '';
    const sku = variant?.variant_sku ?? p.sku ?? '';
    if (!this.client || !this.conversationId || sku === '' || this.addingProductId !== null) {
      return;
    }
    this.addingProductId = p.product_id;
    try {
      const result = await executeShopCartOp(endpoint, { op: 'add', items: [{ sku, qty: 1 }] });
      // One event POST for every outcome — status/result/message all come from
      // toEventOutcome, same mapping the LLM tool path uses (executeCartOp).
      const event = await this.client.postExternalCartEvent(this.conversationId, {
        op: 'add',
        items: [{ sku, qty: 1 }],
        ...toEventOutcome(result),
      });
      if (result.status === 'error') {
        this.lastError = this.t.externalCartFailed;
        return;
      }
      if (result.status === 'rejected') {
        const reason = sanitizeShopMessage(result.message) || this.t.externalCartRejected || this.t.externalCartFailed;
        this.lastError = reason;
        await this.appendShopCartFeedback(event, reason);
        return;
      }
      // Breadcrumb z serwera (templated, user-role) — parytet z builtin addToCart.
      await this.appendShopCartFeedback(event, this.t.externalCartAdded);
      // Każdy świadomy klik „Dodaj" może przekierować (własny klucz per klik).
      this.maybeRedirectAfterAdd(`card-${p.product_id}-${Date.now()}`, [{ op: 'add', status: 'ok' }]);
    } finally {
      this.addingProductId = null;
    }
  }

  /**
   * Append the card-path feedback breadcrumb: the server's templated message
   * when the event POST succeeded, a local fallback otherwise (network/session
   * failure must never leave a silent click — ok and rejected both need a
   * visible line so the customer doesn't re-click into a duplicate add).
   * Shared by the ok and rejected branches of `addToShopCart`.
   */
  private async appendShopCartFeedback(
    event: { message?: StoredMessage } | null,
    fallbackContent: string,
  ): Promise<void> {
    const feedback = event?.message
      ? { id: event.message.id, content: event.message.content }
      : { id: `local-${Date.now()}`, content: fallbackContent };
    this.messages = [...this.messages, { id: feedback.id, role: 'user', content: feedback.content }];
    this.nextScroll = 'bottom';
    await this.updateComplete;
    this.scrollToBottom();
  }

  /**
   * Execute this turn's pending shop-cart ops exactly once. Dedup layers:
   * server-side status (final states never re-run) + a local mark-before-fetch
   * guard covering the pending window across reloads/refetches. An op claimed
   * earlier but still pending server-side → honest error chip (outcome unknown).
   */
  private runPendingCartOps(messageId: string, ops: CartOp[] | null | undefined): void {
    if (!ops || ops.length === 0) {
      return;
    }
    if (!this.externalCartEnabled()) {
      // Serwer wydał pending op, a lokalny config mówi „koszyk wyłączony" — config
      // z bootstrapu jest nieaktualny (plan/moduł zmieniony przy otwartej stronie;
      // prod 01a154ee). Raz odświeżamy; nadal wyłączony → uczciwy chip błędu
      // zamiast wiecznego „aktualizowanie koszyka".
      if (!ops.some((op) => op.status === 'pending')) {
        return;
      }
      void this.refreshExternalCartConfig().then(() => {
        if (this.externalCartEnabled()) {
          this.runPendingCartOps(messageId, ops);
          return;
        }
        for (const op of ops) {
          if (op.status === 'pending') {
            this.setCartOpStatus(messageId, op.invocation_id, { status: 'error' });
          }
        }
      });
      return;
    }
    const claimed: CartOp[] = [];
    for (const op of ops) {
      if (op.status !== 'pending') {
        continue;
      }
      if (!claimCartOpOnce(this.widgetKey, op.invocation_id)) {
        this.setCartOpStatus(messageId, op.invocation_id, { status: 'error' });
        continue;
      }
      claimed.push(op);
    }
    if (claimed.length === 0) {
      return;
    }
    // Opy wykonujemy SEKWENCYJNIE — równoległe POSTy do jednego koszyka
    // wywoływały u sklepu wyścig optimistic-lock na zamówieniu (Drupal Commerce
    // OrderVersionMismatchException, watchdog mojekurczaki 2026-08-11).
    // Redirect dopiero gdy WSZYSTKIE świeżo wykonane opy tej wiadomości się
    // rozstrzygną (chip sukcesu widoczny) — nigdy przy restore (finalne opy i
    // przegrane claimy nie trafiają do `claimed`).
    void (async () => {
      const results: { op: string; status: 'ok' | 'rejected' | 'error' }[] = [];
      for (const op of claimed) {
        results.push(await this.executeCartOp(messageId, op));
      }
      this.maybeRedirectAfterAdd(messageId, results);
    })();
  }

  /**
   * Toggle „Przekieruj do koszyka po dodaniu": po ≥1 udanym addzie nawiguje na
   * stronę koszyka (walidacja celu 1:1 z renderCartBar). Once-per-key; 1200 ms
   * opóźnienia, żeby chip sukcesu zdążył być widoczny. Mobile: mirror
   * onContentLinkNavigate — panel zamyka się przed nawigacją.
   */
  private maybeRedirectAfterAdd(key: string, results: { op: string; status: string }[]): void {
    if (this.config?.external_cart?.redirect_after_add !== true) return;
    if (!results.some((r) => r.op === 'add' && r.status === 'ok')) return;
    // Mixed-outcome turn (multi-op — the orchestrator accumulates ops across
    // tool-loop iterations): a rejection anywhere in the SAME turn must keep
    // the customer on the page long enough to read it — redirect never fires
    // alongside a rejected add, even when another add in the same turn succeeded.
    if (results.some((r) => r.op === 'add' && r.status === 'rejected')) return;
    if (this.redirectedCartMessages.has(key)) return;
    this.redirectedCartMessages.add(key);
    const resolved = resolveShopEndpoint(this.config?.external_cart?.cart_url ?? '', window.location.origin);
    if (resolved === null) return;
    const href = safeUrl(resolved);
    if (href === '') return;
    this.cartRedirectPending = true;
    this.cartRedirectTimer = window.setTimeout(() => {
      this.cartRedirectTimer = null;
      if (window.matchMedia(MOBILE_BREAKPOINT).matches) {
        this.closePanel();
      }
      this.navigateTo(href);
    }, 1200);
  }

  /** Jedyne miejsce nawigacji full-page w widgecie (stubowalne w testach). */
  protected navigateTo(url: string): void {
    window.location.assign(url);
  }

  private async executeCartOp(
    messageId: string,
    op: CartOp,
  ): Promise<{ op: string; status: 'ok' | 'rejected' | 'error' }> {
    const endpoint = this.config?.external_cart?.endpoint_url ?? '';
    const result = await executeShopCartOp(endpoint, {
      op: op.op,
      items: op.items.map(({ sku, qty }) => ({ sku, qty })),
    });
    // Snapshot zawartości tylko dla podglądu — karta renderuje się z opa,
    // a serwer utrwala go na cart_ops (restore po reloadzie bez re-fetchu).
    const snapshot = op.op === 'view' && result.status === 'ok' ? result.cart : undefined;
    const outcome = toEventOutcome(result);
    this.setCartOpStatus(messageId, op.invocation_id, { ...outcome, ...(snapshot ? { cart: snapshot } : {}) });
    if (this.client && this.conversationId) {
      void this.client.postExternalCartEvent(this.conversationId, {
        invocation_id: op.invocation_id,
        ...outcome,
        ...(snapshot ? { cart: snapshot } : {}),
      });
    }
    return { op: op.op, status: result.status };
  }

  private setCartOpStatus(
    messageId: string,
    invocationId: string,
    patch: { status: 'ok' | 'error'; result?: 'rejected'; message?: string; cart?: CartSnapshot },
  ): void {
    this.messages = this.messages.map((m) =>
      m.id !== messageId
        ? m
        : {
            ...m,
            cartOps: (m.cartOps ?? []).map((o) =>
              o.invocation_id === invocationId ? { ...o, ...patch } : o,
            ),
          },
    );
  }

  /** Deterministic status chips for a message's shop-cart ops — the UI (never the LLM) claims success. */
  private renderCartOpChips(ops: CartOp[]) {
    return html`
      <div class="cart-op-chips">
        ${ops.map((op) => {
          if (op.op === 'view') {
            if (op.status === 'pending') {
              return html`<span class="cart-op-chip pending">${this.t.externalCartViewPending}</span>`;
            }
            if (op.status === 'ok') {
              return this.renderCartViewCard(op);
            }
            return html`<span class="cart-op-chip error">${this.t.externalCartViewFailed}</span>`;
          }
          const count = op.items.reduce((sum, i) => sum + i.qty, 0);
          if (op.status === 'pending') {
            return html`<span class="cart-op-chip pending">${this.t.externalCartPending}</span>`;
          }
          if (op.status === 'ok' && op.result === 'rejected') {
            // op.message is raw on a live resolve, already-clean on a server
            // replay — sanitize is idempotent, so this is safe either way.
            return html`<span class="cart-op-chip rejected"
              >${sanitizeShopMessage(op.message) || this.t.externalCartRejected || this.t.externalCartFailed}</span
            >`;
          }
          if (op.status === 'ok') {
            const label =
              op.op === 'remove'
                ? this.t.externalCartRemoved
                : op.op === 'clear'
                  ? this.t.externalCartCleared
                  : this.t.externalCartAdded;
            // Suma SZTUK, nie liczba produktów — „12 produktów" przy 3 produktach
            // × 4 szt. wprowadzało w błąd (prod 2026-08-25); jednostka „szt."
            // jest jednoznaczna niezależnie od rozkładu pozycji.
            const counter =
              op.op === 'add' && count > 0 ? ` · ${count} ${this.t.cartQtyPieces}` : '';
            return html`<span class="cart-op-chip ok">${label}${counter}</span>`;
          }
          return html`<span class="cart-op-chip error">${this.t.externalCartFailed}</span>`;
        })}
      </div>
    `;
  }

  /**
   * Czy wiadomość w ogóle trafia na listę. Puste dymki asystenta są pomijane:
   * placeholder streamingu przed pierwszym tokenem i resztki po błędzie streamu
   * albo fallbacku 404→sync. Stan oczekiwania pokazuje wskaźnik pisania.
   */
  private isRenderableMessage(m: ChatMessage): boolean {
    return (
      m.role !== 'assistant' ||
      m.content !== '' ||
      !!m.guardrail ||
      (m.products?.length ?? 0) > 0 ||
      (m.cartOps?.length ?? 0) > 0
    );
  }

  /**
   * Czy kotwica wskazuje wiadomość, która REALNIE się wyrenderuje. Sprawdzamy to
   * tym samym predykatem co lista — inaczej formularz przypięty do odfiltrowanego
   * dymka zniknąłby z ekranu (kotwica jest, render nie). Gdy kotwicy brak albo
   * wypadła z listy (rozmowa przywrócona po reloadzie — serwer mówi „proszę
   * o kontakt", ale nie wiadomo przy której wiadomości) → render na końcu, jak dotąd.
   */
  private hasAnchoredMessage(anchorId: string | null): boolean {
    return (
      anchorId !== null &&
      this.messages.some((m) => m.id === anchorId && m.role === 'assistant' && this.isRenderableMessage(m))
    );
  }

  /**
   * Kotwica dla prośby o formularz: PIERWSZA odpowiedź asystenta z
   * created_at >= requested_at = tura, która o formularz poprosiła (marker
   * zapisuje się przed persystencją tej odpowiedzi). Dzięki temu po reloadzie
   * formularz wraca na swoje pierwotne miejsce zamiast spadać na dno
   * (zgłoszenie 2026-09-01). W turze NA ŻYWO placeholder nie ma jeszcze
   * created_at → fallback: ostatnia odpowiedź (dotychczasowe zachowanie).
   */
  private anchorForRequest(requestedAt: string | null): string | null {
    if (requestedAt) {
      const t = Date.parse(requestedAt);
      if (!Number.isNaN(t)) {
        for (const m of this.messages) {
          if (m.role === 'assistant' && m.created_at && Date.parse(m.created_at) >= t) {
            return m.id;
          }
        }
      }
    }

    return this.lastAssistantMessageId();
  }

  /** Id ostatniego dymka asystenta — przy nim bot poprosił o kontakt/eskalację. */
  private lastAssistantMessageId(): string | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const m = this.messages[i];
      if (m !== undefined && m.role === 'assistant') return m.id;
    }

    return null;
  }

  /**
   * Deterministic cart-contents card for a resolved 'view' op — rendered from
   * the shop's snapshot, never the LLM. `items` absent = shop doesn't expose
   * contents; [] = empty cart. Names are shop/catalog data → text bindings only.
   */
  private renderCartViewCard(op: CartOp) {
    const cart = op.cart;
    const items = cart?.items;
    if (!cart || items === undefined) {
      return html`<div class="cart-view-card"><div class="cv-note">${this.t.externalCartViewUnavailable}</div></div>`;
    }
    if (items.length === 0) {
      return html`<div class="cart-view-card"><div class="cv-note">${this.t.externalCartViewEmpty}</div></div>`;
    }
    const count = cart.count ?? items.reduce((sum, i) => sum + (i.qty ?? 1), 0);
    // BEZ wiersza „Razem": total_cents przychodzi od SKLEPU i bywa kwotą do zapłaty
    // razem z dostawą, na którą bot nie ma wpływu — pokazywanie jej w karcie czatu
    // wprowadzało klienta w błąd. Sumę widzi w koszyku sklepu, gdzie jest prawdziwa.
    return html`
      <div class="cart-view-card">
        <div class="cv-head">
          <span class="cv-title">${this.t.externalCartViewTitle}</span>
          <span class="cv-count">${formatCartCounter(this.locale, count, this.t.cartCounter)}</span>
        </div>
        ${items.map(
          (i) => html`
            <div class="cv-item">
              <span class="cv-name">${i.name && i.name !== '' ? i.name : i.sku}</span>
              <span class="cv-line">${this.formatCartLine(i.qty ?? 1, i.line_total_cents, cart.currency ?? '')}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  /**
   * Ilość + kwota LINII w jednym, jednoznacznym stringu: „2 szt. · 84,00 PLN".
   *
   * Dawniej były to dwa osobne spany („× 1" i „253.00 PLN"), co w jednej linii
   * czytało się jak jedna liczba („1253 PLN") — stąd ilość z przodu i wyraźny
   * separator.
   *
   * Ceny jednostkowej NIE wyliczamy z sumy linii, choć kusi: snapshot sklepu jej
   * nie niesie, a przy rabacie ilościowym iloraz jest kwotą, której sklep nigdy
   * nie podał — i przeczyłby cenie katalogowej, którą bot cytuje w tej samej
   * rozmowie. Pokazujemy więc wyłącznie liczby pochodzące od sklepu.
   */
  private formatCartLine(qty: number, lineTotalCents: number | null | undefined, currency: string): string {
    const pieces = `${qty} ${this.t.cartQtyPieces}`;
    if (typeof lineTotalCents !== 'number') {
      return pieces;
    }

    // Brak waluty w snapshocie → sama liczba byłaby myląca; wtedy tylko sztuki.
    const amount = currency === '' ? null : this.formatCents(lineTotalCents, currency);

    return amount === null ? pieces : `${pieces} · ${amount}`;
  }

  protected willUpdate(changed: Map<string, unknown>): void {
    // Auto-close the sheet when the order stops being an open draft (submit,
    // server clear, or it empties while open) — keeps the `!orderFormOpen` chat
    // gates honest and avoids rendering a sheet with no items.
    if (changed.has('order')) {
      const open = this.order && this.order.status === 'draft' && this.order.items.length > 0;
      if (!open) {
        // Draft gone (submitted / cleared / emptied): a fresh order should prefill again,
        // and the sheet must close so the `!orderFormOpen` chat gates stay honest. The typed
        // `orderFormData` survives in state, so a close→reopen on a still-live draft (which
        // keeps `open` true and skips this branch) preserves the user's input.
        this.orderFormDirty = false;
        if (this.cartSheetOpen) {
          this.cartSheetOpen = false;
          this.orderFormOpen = false;
        }
      }
    }
  }

  protected updated(changed: Map<string, unknown>): void {
    // Dark stylesheet must be reconciled even while closed — the mobile peek
    // shows surfaces before the panel opens.
    this.syncDarkStylesheet();

    if (!this.isOpen) return;

    // Controlled dial-code <select>: a `.value` property binding can't drive it —
    // lit-html commits the select's binding BEFORE its <option> children exist, so
    // the value is dropped and the first option (ascending order → +1 US) shows
    // regardless of the bot's configured country. Reconcile imperatively after
    // render, when the options are present, from each select's data-cc. After the
    // isOpen guard — the phone forms exist only while open, so no scanning when
    // closed / on background renders.
    this.renderRoot.querySelectorAll<HTMLSelectElement>('select.phone-cc').forEach((s) => {
      const cc = s.dataset.cc;
      if (cc !== undefined && cc !== '' && s.value !== cc) s.value = cc;
    });
    // Skip per-keystroke updates from `inputValue` and `newOrigin`-like state —
    // only scroll when the visible thread actually grew/changed.
    if (changed.has('messages') || changed.has('isLoading') || changed.has('order') || changed.has('isOpen')) {
      this.autoScroll();
    }
    // Po zakończeniu odpowiedzi input był `disabled` (przeglądarka odbiera mu
    // focus), więc przywracamy go — user pisze kolejną wiadomość bez klikania.
    if (changed.has('isLoading') && !this.isLoading && !this.orderFormOpen && !this.cartSheetOpen) {
      this.renderRoot.querySelector<HTMLInputElement>('.input')?.focus();
    }
    // Po otwarciu panelu ustawiamy focus w polu wiadomości — ale TYLKO na desktopie.
    // Na dotyku (iOS Safari) programowy focus() pokazuje klawiaturę, ale NIE przewija
    // pola nad nią — to robi wyłącznie prawdziwy tap palcem — więc input zostaje
    // schowany za klawiaturą (czat się „nie podnosi"). Na dotyku zostawiamy fokus
    // tapnięciu użytkownika (które działa poprawnie).
    const coarsePointer = typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    if (changed.has('isOpen') && this.isOpen && !this.isLoading && !this.orderFormOpen && !this.cartSheetOpen && !coarsePointer) {
      this.renderRoot.querySelector<HTMLInputElement>('.input')?.focus();
    }
    // Cart sheet: focus into it on open, return focus to the bar on close.
    if (changed.has('cartSheetOpen')) {
      // preventScroll: focusing the sheet (bottom of the panel) would otherwise scroll
      // the panel to "reveal" it, shoving the chat up — overflow:hidden still scrolls on focus.
      if (this.cartSheetOpen) this.sheetRef.value?.focus({ preventScroll: true });
      else this.renderRoot.querySelector<HTMLElement>('.cart-bar')?.focus({ preventScroll: true });
    }
    // Refresh the scroll fog after the open/step/contents settle — only while the sheet is
    // actually visible (skip the forced layout reads on every `order` change when closed),
    // and only for the step on screen.
    if (this.cartSheetOpen && (changed.has('cartSheetOpen') || changed.has('orderFormOpen') || changed.has('order'))) {
      requestAnimationFrame(() => {
        if (this.orderFormOpen) this.updateFog(this.formScrollRef.value, this.formFogRef.value);
        else this.updateFog(this.cartScrollRef.value, this.cartFogRef.value);
      });
    }
  }

  private renderPeek() {
    const botName = this.config?.bot.name ?? 'Chat';
    const greeting = this.config?.bot.greeting?.trim() || this.t.peekGreetingFallback;
    return html`
      <div class="peek-sheet" role="dialog" aria-label=${botName}>
        <button
          class="x-close peek-close"
          @click=${this.dismissPeek}
          aria-label=${this.t.closeChat}
        >${ICON_CLOSE}</button>
        <p class="peek-greeting">${greeting}</p>
        <button class="peek-cta" @click=${this.openFromPeek}>
          ${interpolate(this.t.peekStartChat, { name: botName })}
        </button>
        ${(this.config?.branding?.show ?? true)
          ? html`<div class="peek-powered">${brandLogo}</div>`
          : nothing}
      </div>
    `;
  }

  /**
   * One function for both variants — island = a modifier class, not a second
   * template. type="button" on BOTH buttons is mandatory: inside <form class="island">
   * a typeless button is a submit and would fire openFromIsland().
   */
  private renderNudge(variant: 'bubble' | 'island') {
    const spec = this.nudge;
    if (spec === null) return nothing;
    return html`
      <div class=${classMap({ nudge: true, 'nudge--island': variant === 'island' })} role="status">
        <button type="button" class="x-close nudge-close" @click=${this.dismissNudge} aria-label=${this.t.nudgeDismiss}>${ICON_CLOSE}</button>
        <button type="button" class="nudge-body" @click=${this.openFromNudge}>${spec.message}</button>
      </div>
    `;
  }

  render() {
    // Owner ukrył widget (Publish page → Widget visibility off lub URL match).
    // Renderujemy `nothing` — zero śladu, ani launcher, ani peek, ani panel.
    if (this.isHidden) return nothing;

    // Pre-bootstrap: nie pokazuj nic. Zapobiega mignięciu launchera przed
    // tym jak dowiemy się że widget powinien być ukryty.
    if (!this.bootstrapAttempted) return nothing;

    if (!this.isOpen) {
      // launcher_display gate (desktop/mobile/hidden) — also suppresses peek/island.
      if (!this.launcherVisibleForViewport()) return nothing;
      // Floating Island entry replaces the corner bubble (and the mobile peek).
      if ((this.config?.theme.entry_style ?? 'bubble') === 'island') {
        return this.renderIsland();
      }
      if (this.peekVisible) {
        return this.renderPeek();
      }
      const iconUrl = this.config?.theme.launcher_icon_url;
      return html`
        ${this.nudgeVisible ? this.renderNudge('bubble') : nothing}
        <button class="launcher" @click=${this.openPanel} aria-label=${this.t.openChat}>
          <span class="lb-ring"></span>
          ${iconUrl
            ? html`<img class="launcher-icon" src=${iconUrl} alt="" />`
            : html`<span class="ai-text">AI</span>`}
        </button>
      `;
    }

    const botName = this.config?.bot.name ?? 'Bot';
    const suggestions = this.config?.bot.suggested_questions ?? [];
    // Hide the start-screen affordances while the order form is open — they are
    // checkout-irrelevant noise and would steal vertical room from the form.
    const showSuggestions = suggestions.length > 0 && this.messages.length <= 1 && !this.orderFormOpen;

    const avatarUrl = this.config?.bot.avatar_url;
    const avatarContent = avatarUrl
      ? html`<img src=${avatarUrl} alt="${botName}">`
      : botName.slice(0, 2).toUpperCase();

    return html`
      <div class="panel" role="dialog" aria-label="Chat ${botName}" @click=${this.onContentLinkNavigate}>
        <header>
          <div class="bot-avatar">${avatarContent}</div>
          <div class="bot-id">
            <div class="bot-name">${botName}</div>
            <div class="bot-status">
              <span class="dot"></span>${this.isLoading ? this.t.typing : this.t.active}
            </div>
          </div>
          <div class="header-actions">
            <button class="header-btn" @click=${this.handleStartNewConversation}
                    title=${this.t.newConversation} aria-label=${this.t.newConversation}>${ICON_NEW_CHAT}</button>
            <button class="close-btn" @click=${this.closePanel} aria-label=${this.t.closeChat}>${ICON_CLOSE}</button>
          </div>
        </header>

        <div class="scroll-area">
          ${repeat(
            // Skip empty assistant bubbles: the streaming placeholder before its
            // first token, and any placeholder left behind by a stream error or
            // 404→sync fallback. The typing indicator covers the waiting state,
            // so no blank bubble flashes above it.
            this.messages.filter((m) => this.isRenderableMessage(m)),
            (m) => m.id,
            (m) => {
              if (m.role !== 'assistant') {
                return html`<div class="msg user">${m.content}</div>`;
              }
              // Bot bubble (no per-message avatar — mockup parity), then product
              // cards as full-width siblings OUTSIDE the bubble.
              const hasText = m.content !== '' || !!m.guardrail;
              return html`
                ${hasText
                  ? html`<div class="msg assistant ${m.guardrail ? 'guardrail' : ''}">
                        ${m.guardrail ? html`<div class="guardrail-badge">${m.guardrail}</div>` : nothing}
                        ${this.renderMessageContent(m.content)}
                      </div>
                      ${this.renderMsgTime(m)}`
                  : nothing}
                ${m.products && m.products.length > 0 ? this.renderProductGroup(m.products) : nothing}
                ${m.cartOps && m.cartOps.length > 0 ? this.renderCartOpChips(m.cartOps) : nothing}
                ${m.id === this.escalationAnchorId ? this.renderEscalationForm() : nothing}
                ${m.id === this.contactAnchorId ? this.renderContactForm() : nothing}
              `;
            },
          )}
          ${this.isLoading
            ? html`
                <div class="typing">
                  <div class="avatar">${botName.slice(0, 2).toUpperCase()}</div>
                  <span class="label">${this.t.responding}</span>
                  <div class="dots"><span></span><span></span><span></span></div>
                </div>
              `
            : nothing}
          ${showSuggestions
            ? html`
                <div class="suggestions">
                  ${suggestions.map(
                    (s) => html`
                      <button class="suggestion" @click=${() => this.send(s)}>${s}</button>
                    `,
                  )}
                </div>
              `
            : nothing}
          ${this.hasAnchoredMessage(this.escalationAnchorId) ? nothing : this.renderEscalationForm()}
          ${this.hasAnchoredMessage(this.contactAnchorId) ? nothing : this.renderContactForm()}
        </div>

        ${this.renderCartBar()}

        ${(this.bootError ?? this.lastError)
          ? html`<div class="error-banner">${this.bootError ?? this.lastError}</div>`
          : nothing}

        ${this.cookieInfoOpen ? this.renderCookieBanner() : nothing}

        ${this.confirmNewOpen ? this.renderConfirmNew() : nothing}

        <footer>
          <form
            class="input-row"
            @submit=${(e: SubmitEvent) => {
              e.preventDefault();
              this.send(this.inputValue);
            }}
          >
            <div class="input-wrap">
              <input
                class="input"
                type="text"
                placeholder=${this.t.inputPlaceholder}
                maxlength="1000"
                .value=${this.inputValue}
                @input=${(e: Event) => (this.inputValue = (e.target as HTMLInputElement).value)}
                ?disabled=${this.demo || this.isLoading || this.bootError !== null}
              />
            </div>
            <button
              class="send-btn"
              type="submit"
              aria-label=${this.t.send}
              ?disabled=${this.demo || this.isLoading || this.bootError !== null || !this.inputValue.trim()}
            >
              ${ICON_SEND}
            </button>
          </form>
        </footer>

        <div class="panel-legal">
          ${(this.config?.branding?.show ?? true)
            ? html`<span class="powered-by">${this.t.poweredBy}</span
                ><a
                  class="powered-link"
                  href=${BRAND_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label=${BRAND_NAME}
                  >${brandLogo}</a
                ><span class="footer-sep">·</span>`
            : nothing}
          <button class="cookie-reopen" type="button" @click=${this.openCookieInfo}>
            ${this.t.cookieReopenLink} <u>${this.t.cookieReopenLinkTerms}</u>.
          </button>
        </div>

        ${this.renderCartSheet()}
      </div>
    `;
  }

  private renderCookieBanner() {
    return html`
      <div class="cookie-banner" role="dialog" aria-label="${this.t.cookieBannerTitle}">
        <h3 class="cookie-title">${this.t.cookieBannerTitle}</h3>
        <div class="cookie-scroll">
          <!-- AI na pierwszym miejscu — to główny powód wejścia w ten panel
               (link "akceptujesz warunki" przy disclaimerze AI). -->
          <h4 class="cookie-section-title">${this.t.cookieAiHeading}</h4>
          <p class="cookie-body">${this.t.cookieAiBody}</p>
          <h4 class="cookie-section-title">${this.t.cookieCookiesHeading}</h4>
          <p class="cookie-body">${this.t.cookieCookiesBody}</p>
          <h4 class="cookie-section-title">${this.t.cookieAdminHeading}</h4>
          <p class="cookie-body">${this.t.cookieAdminBody}</p>
        </div>
        <div class="cookie-actions">
          <button class="cookie-btn cookie-btn-primary" type="button" @click=${this.closeCookieInfo}>
            ${this.t.cookieClose}
          </button>
        </div>
      </div>
    `;
  }

  /**
   * In-widget confirmation for "New conversation" — a centered modal over a scrim,
   * scoped to the chat window (NOT a system confirm()). Clicking the scrim or
   * "Cancel" dismisses; "Start new" wipes the thread.
   */
  private renderConfirmNew() {
    return html`
      <div
        class="confirm-scrim"
        @click=${(e: Event) => { if (e.target === e.currentTarget) this.cancelNewConversation(); }}
      >
        <div class="confirm-modal" role="dialog" aria-modal="true" aria-label=${this.t.newConversationTitle}>
          <h3 class="confirm-title">${this.t.newConversationTitle}</h3>
          <p class="confirm-body">${this.t.newConversationConfirm}</p>
          <div class="confirm-actions">
            <button class="confirm-btn confirm-cancel" type="button" @click=${this.cancelNewConversation}>
              ${this.t.newConversationCancel}
            </button>
            <button class="confirm-btn confirm-ok" type="button" @click=${this.confirmNewConversation}>
              ${this.t.newConversationConfirmBtn}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

// Custom element tag is brand-derived at build time (WIDGET_TAG), so it can't be
// a literal key in HTMLElementTagNameMap — the augmentation is intentionally omitted.
